import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from '../axiosConfig';
import Modal from '../components/Modal';
import AccountSelector from '../components/AccountSelector';
import { IconSave, IconTick, IconLink, IconUndo, IconRedo, IconExport, IconClear, IconX, IconMove, IconClock, IconCopy, IconEdit, IconFlag, IconView, useFieldOptions, fieldOptionList, MONTH_LABELS, typeIcons } from '../components/Utils';
import Tooltip from '../components/Tooltip';
import usePopoverPosition from '../components/usePopoverPosition';
import { FaExclamationTriangle, FaEllipsisH, FaEyeSlash, FaRegImage, FaArrowUp, FaArrowDown, FaImage, FaLayerGroup, FaClipboardList, FaTrashAlt } from 'react-icons/fa';
import { GiBrickWall } from 'react-icons/gi';
import { MdDonutSmall } from 'react-icons/md';
import SegmentedControl from '../components/SegmentedControl';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  SOURCE_TABS, DEFAULT_SOURCE_TAB, TAB_FILTER_FIELDS, TABS_WITH_FLAG_FILTER, TYPE_LABELS,
  isArchivedRow, isSourceTab, rowsForTab, rowFlagNames, matchesFlagSelection, matchesNameSearch,
  filterOptions, applyRowFilters, formatArchivedDate,
  SORT_OPTIONS, DEFAULT_SORT, sortKeysForTab, effectiveSort, nextSort, sortSourceItems,
  slideDisplayFlags, rowDisplayFlags,
  compareNames,
} from './programmeBuilder/sourceFilters';
import { useTaskRefresh } from '../context/TaskRefreshContext.jsx';
import './ProgrammeBuilder.css';
import '../components/Table.css';
import Checkbox from '../components/Checkbox';
import PageTitle from "../components/PageTitle";

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

// Writes both plain-text and HTML clipboard flavors so pasting into rich-text
// targets (email, Docs, Slack) preserves bold/italic formatting, while plain-text
// targets still get clean plain text. Falls back to plain text if the richer
// Clipboard API is unavailable.
function copyRichText(text, html) {
  if (navigator.clipboard && typeof window.ClipboardItem !== 'undefined') {
    const item = new window.ClipboardItem({
      'text/plain': new Blob([text], { type: 'text/plain' }),
      'text/html': new Blob([html], { type: 'text/html' }),
    });
    navigator.clipboard.write([item]).catch(() => {
      navigator.clipboard.writeText(text).catch(() => {});
    });
  } else {
    navigator.clipboard.writeText(text).catch(() => {});
  }
}

// Blocks and modules should each have a positive duration (minutes). For a module
// this is its own Duration field, not the sum of its blocks.
const isMissingDuration = (item) => {
  const raw = item?.duration;
  const n = raw === '' || raw === null || raw === undefined ? NaN : Number(raw);
  return !Number.isFinite(n) || n <= 0;
};

// The one warning symbol used across the programme builder; `text` is shown in
// a warning tooltip on hover/focus.
const WarningIcon = ({ text, className }) => (
  <Tooltip text={text} variant="warning">
    <FaExclamationTriangle
      className={`pb-warning-symbol${className ? ` ${className}` : ''}`}
      aria-label={text}
      role="img"
      tabIndex={0}
    />
  </Tooltip>
);

// Shown next to a block/module name when it has no duration.
const MissingDurationIcon = ({ type }) => (
  <WarningIcon
    text={`${type === 'module' ? 'Module' : 'Block'} has no duration`}
    className="pb-warning-symbol-after"
  />
);

// Which programme_details metadata fields apply to each saveable type. Part and
// module programmes use none of them (just Name); a module's duration and
// eLearning courses belong to the module itself (edited on the canvas).
// Template and custom programmes have the same fields (and show them the same
// way): they differ only in that saving a custom one breaks its links to saved
// components (the backend stores copies). 'modules' isn't a saved field: it shows
// the programme's modules and their durations (see modulesSummary), which are
// edited on each module. module_duration is the programme's own free-text
// "Module Duration" (e.g. "3.5 hours"), kept for now (shown as "Temporary").
const PROGRAMME_FIELDS = ['month', 'year', 'account', 'audience', 'access', 'delivery', 'modules', 'module_duration'];
const PROGRAMME_META_FIELDS_BY_TYPE = {
  block: ['audience', 'delivery'],
  part: [],
  module: [],
  template: PROGRAMME_FIELDS,
  custom: PROGRAMME_FIELDS,
};

// delivery/access/audience arrive from the backend as text labels (options from
// GET /field-options/). A non-string value can only be a stale copy of a programme
// cached in localStorage from before that, so it's treated as unset.
const codedText = (value) => (typeof value === 'string' && value) || null;
const deliveryLabel = codedText;

const emptyProgrammeMeta = () => ({
  audience: '',
  delivery: '',
  access: '',
  account: null,
  month: '',
  year: '',
  module_duration: '',
});

// Pull the metadata fields off a programme row (as returned by GET /programmes) into the
// shape the save/overwrite/edit forms keep in state.
const metaFromProgramme = (prog) => ({
  audience: prog?.audience ?? '',
  delivery: codedText(prog?.delivery) ?? '',
  access: codedText(prog?.access) ?? '',
  account: prog?.account ?? null,
  month: prog?.month ?? '',
  year: prog?.year ?? '',
  module_duration: prog?.module_duration ?? '',
});

// Build the subset of metadata fields relevant to `type` for a save/overwrite/edit payload.
const metaPayloadForType = (type, meta) => {
  const fields = PROGRAMME_META_FIELDS_BY_TYPE[type];
  if (!fields) return {};
  const payload = {};
  for (const field of fields) {
    if (field === 'modules') continue; // shown, not saved
    if (field === 'account') payload[field] = meta.account ?? null;
    else if (field === 'module_duration') payload[field] = String(meta.module_duration ?? '').trim() || null;
    else payload[field] = meta[field] === '' ? null : meta[field];
  }
  return payload;
};

// Total duration (minutes) of the blocks anywhere in `items`.
function sumDescendantBlockDurations(items) {
  if (!Array.isArray(items) || items.length === 0) return 0;
  let total = 0;
  for (const item of items) {
    if (!item || typeof item !== 'object') continue;

    if (item.type === 'block') {
      const parsed = parseFloat(item.duration);
      if (!isNaN(parsed)) total += parsed;
    }

    if (Array.isArray(item.content) && item.content.length) {
      total += sumDescendantBlockDurations(item.content);
    }
  }
  return total;
}

// A module's duration in minutes: its own if set, otherwise the sum of its
// blocks' durations (0 if neither).
const moduleMinutes = (item) => (
  parseFloat(item?.duration) > 0 ? parseFloat(item.duration) : sumDescendantBlockDurations(item?.content)
);

// Minutes as hours for display: 150 -> "2.5", 120 -> "2", 95 -> "1.58".
const formatHours = (minutes) => String(Math.round((minutes / 60) * 100) / 100);

// The modules in a programme's top-level content and their durations, grouped
// by duration in order of first appearance: "2 x 2 Hr Modules",
// "2.5 Hr + 3 Hr Modules", "3 x 4 Hr + 2 x 1 Hr Modules".
function modulesSummary(content) {
  const modules = (Array.isArray(content) ? content : []).filter((item) => item?.type === 'module');
  if (modules.length === 0) return 'No modules';
  const groups = [];
  let missing = 0;
  for (const mod of modules) {
    const minutes = moduleMinutes(mod);
    if (!(minutes > 0)) {
      missing += 1;
      continue;
    }
    const hours = formatHours(minutes);
    const group = groups.find((g) => g.hours === hours);
    if (group) group.count += 1;
    else groups.push({ hours, count: 1 });
  }
  const noun = modules.length === 1 ? 'Module' : 'Modules';
  if (groups.length === 0) return `${modules.length} ${noun} with no duration`;
  const text = `${groups.map((g) => (g.count > 1 ? `${g.count} x ${g.hours} Hr` : `${g.hours} Hr`)).join(' + ')} ${noun}`;
  return missing ? `${text} (${missing} with no duration)` : text;
}

// Minutes <-> the hours shown in a module's Duration field (nearest 0.5 hour).
const minutesToHours = (minutes) => {
  const n = Number(minutes);
  return minutes === '' || minutes === null || minutes === undefined || !Number.isFinite(n) ? '' : String(n / 60);
};
const hoursToMinutes = (hours) => {
  const n = parseFloat(hours);
  return Number.isFinite(n) ? Math.round(n * 2) * 30 : '';
};

// A module's Duration field: typed in hours (to the nearest 0.5), held as minutes
// like every other duration (the backend also rounds module durations to the
// half hour). Keeps its own text so "1." can be typed on the way to "1.5"; it
// shows the rounded value once the field loses focus.
function ModuleHoursInput({ id, minutes, onChange, disabled }) {
  const [text, setText] = useState(() => minutesToHours(minutes));
  useEffect(() => {
    // Changed from outside (e.g. the form reset or loaded another module).
    if (hoursToMinutes(text) !== (minutes === '' || minutes == null ? '' : Number(minutes))) {
      setText(minutesToHours(minutes));
    }
  }, [minutes]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <input autoComplete="off"
      id={id}
      type="number"
      min="0"
      step="0.5"
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        onChange(hoursToMinutes(e.target.value));
      }}
      onBlur={() => setText(minutesToHours(hoursToMinutes(text)))}
      className="pb-form-input"
      placeholder="Enter duration in hours, e.g. 1.5"
      disabled={disabled}
    />
  );
}

// The block/module's own fields, as in the canvas's Edit Block / Edit Module
// modal: Duration (blocks and modules) and eLearning courses (modules; up to 5).
// Parts have none. Shared by that modal and the Save / Overwrite / Edit Details
// forms for saved blocks and modules. `value` is { duration, bundled_course_ids },
// duration in minutes (a module's is entered in hours, see ModuleHoursInput).
function ComponentDetailFields({ idPrefix, type, value, onChange, courses, disabled }) {
  if (type !== 'block' && type !== 'module') return null;
  const selectedIds = value?.bundled_course_ids || [];
  return (
    <>
      <div className="pb-form-group">
        <label htmlFor={`${idPrefix}-duration`} className="pb-form-label">
          {type === 'module' ? 'Duration (hours):' : 'Estimated Duration (minutes):'}
        </label>
        {type === 'module' ? (
          <ModuleHoursInput
            id={`${idPrefix}-duration`}
            minutes={value?.duration ?? ''}
            onChange={(duration) => onChange({ ...value, duration })}
            disabled={disabled}
          />
        ) : (
          <input autoComplete="off"
            id={`${idPrefix}-duration`}
            type="number"
            min="0"
            value={value?.duration ?? ''}
            onChange={(e) => onChange({ ...value, duration: e.target.value })}
            className="pb-form-input"
            placeholder="Enter duration in minutes"
            disabled={disabled}
          />
        )}
      </div>

      {type === 'module' && (
        <div className="pb-form-group">
          <label className="pb-form-label">eLearning Courses:</label>
          {Array.from({ length: Math.min(selectedIds.length + 1, 5) }).map((_, idx) => {
            const currentValue = selectedIds[idx] ?? '';
            const otherSelectedIds = selectedIds.filter((_, i) => i !== idx);
            return (
              <select autoComplete="off"
                key={idx}
                value={currentValue}
                onChange={(e) => {
                  const val = e.target.value;
                  const newIds = [...selectedIds];
                  if (val === '') {
                    newIds.splice(idx);
                  } else {
                    newIds[idx] = parseInt(val, 10);
                    newIds.splice(idx + 1);
                  }
                  onChange({ ...value, bundled_course_ids: newIds });
                }}
                className="pb-form-input"
                style={{ marginBottom: '6px' }}
                disabled={disabled}
              >
                <option value="">Select course</option>
                {(courses || []).map(course => (
                  <option
                    key={course.id}
                    value={course.id}
                    disabled={otherSelectedIds.includes(course.id)}
                  >
                    {course.title}
                  </option>
                ))}
              </select>
            );
          })}
        </div>
      )}
    </>
  );
}

// { duration, bundled_course_ids } of a block/module item (for ComponentDetailFields).
const componentDetailsOf = (item) => ({
  duration: item?.duration ?? '',
  bundled_course_ids: Array.isArray(item?.bundled_course_ids) ? item.bundled_course_ids : [],
});

// The canvas content to save as a block/module programme, with the Save form's
// component fields applied to its (single) top-level block/module.
const withTopLevelDetails = (content, type, details) => {
  const list = Array.isArray(content) ? content : [];
  if ((type !== 'block' && type !== 'module') || list.length !== 1 || list[0]?.type !== type) return list;
  return [{
    ...list[0],
    duration: details.duration,
    ...(type === 'module' ? { bundled_course_ids: details.bundled_course_ids || [] } : {}),
  }];
};

// Renders the type-dependent metadata fields(audience/delivery/duration/etc.) shared by
// the "Save new", "Save (overwrite existing)", and "Edit details" forms.
// `content` is the programme's top-level content, for the modules summary (null
// while it loads).
function ProgrammeMetaFields({ idPrefix, type, meta, onChange, disabled, content }) {
  const fieldOptions = useFieldOptions();
  const fields = PROGRAMME_META_FIELDS_BY_TYPE[type];
  if (!fields) return null;

  const set = (field) => (e) => onChange({ ...meta, [field]: e.target.value });

  return (
    <>
      {fields.includes('month') && (
        <div className="pb-form-group">
          <label htmlFor={`${idPrefix}-month`} className="pb-form-label">Month:</label>
          <input autoComplete="off"
            id={`${idPrefix}-month`}
            type="number"
            min="1"
            max="12"
            value={meta.month ?? ''}
            onChange={set('month')}
            className="pb-form-input"
            placeholder="Month"
            disabled={disabled}
          />
        </div>
      )}

      {fields.includes('year') && (
        <div className="pb-form-group">
          <label htmlFor={`${idPrefix}-year`} className="pb-form-label">Year:</label>
          <input autoComplete="off"
            id={`${idPrefix}-year`}
            type="number"
            value={meta.year ?? ''}
            onChange={set('year')}
            className="pb-form-input"
            placeholder="Year"
            disabled={disabled}
          />
        </div>
      )}

      {fields.includes('account') && (
        <div className="pb-form-group">
          <label htmlFor={`${idPrefix}-account`} className="pb-form-label">Account:</label>
          <AccountSelector
            value={meta.account}
            onChange={(account) => onChange({ ...meta, account: account?.id ?? null })}
          />
        </div>
      )}

      {fields.includes('audience') && (
        <div className="pb-form-group">
          <label htmlFor={`${idPrefix}-audience`} className="pb-form-label">Audience:</label>
          <select autoComplete="off"
            id={`${idPrefix}-audience`}
            value={meta.audience ?? ''}
            onChange={set('audience')}
            className="pb-form-input"
            disabled={disabled}
          >
            <option value="">Select...</option>
            {fieldOptionList(fieldOptions, 'programme_details', 'audience').map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      )}

      {fields.includes('access') && (
        <div className="pb-form-group">
          <label htmlFor={`${idPrefix}-access`} className="pb-form-label">Access:</label>
          <select autoComplete="off"
            id={`${idPrefix}-access`}
            value={meta.access ?? ''}
            onChange={set('access')}
            className="pb-form-input"
            disabled={disabled}
          >
            <option value="">Select...</option>
            {fieldOptionList(fieldOptions, 'programme_details', 'access').map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      )}

      {fields.includes('delivery') && (
        <div className="pb-form-group">
          <label htmlFor={`${idPrefix}-delivery`} className="pb-form-label">Delivery:</label>
          <select autoComplete="off"
            id={`${idPrefix}-delivery`}
            value={meta.delivery ?? ''}
            onChange={set('delivery')}
            className="pb-form-input"
            disabled={disabled}
          >
            <option value="">Select...</option>
            {fieldOptionList(fieldOptions, 'programme_details', 'delivery').map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      )}

      {fields.includes('modules') && (
        <div className="pb-form-group">
          <span className="pb-form-label">Modules:</span>
          <div className="pb-modules-summary">
            {content ? modulesSummary(content) : 'Loading…'}
          </div>
          <p className="pb-form-hint pb-modules-summary-hint">
            Edit individual modules to change their durations
          </p>
        </div>
      )}

      {fields.includes('module_duration') && (
        <div className="pb-form-group">
          <label htmlFor={`${idPrefix}-module-duration`} className="pb-form-label">Module Duration (Temporary):</label>
          <input autoComplete="off"
            id={`${idPrefix}-module-duration`}
            type="text"
            value={meta.module_duration ?? ''}
            onChange={set('module_duration')}
            className="pb-form-input"
            placeholder="e.g. 3.5 hours"
            disabled={disabled}
          />
        </div>
      )}

    </>
  );
}

export default function ProgrammeBuilder() {
  const refreshTasks = useTaskRefresh();
  const linkedComponentTooltip = "This component is linked. When used in a programme, any changes to the saved component will be updated in the programme. If this component is changed in a programme it will become unlinked.";

  const rightPaneRef = useRef(null);
  const [isRightPaneNarrow, setIsRightPaneNarrow] = useState(false);

  useEffect(() => {
    const update = () => {
      const el = rightPaneRef.current;
      if (!el) return;
      const width = el.getBoundingClientRect().width;
      setIsRightPaneNarrow(width < 500);
    };

    update();

    const el = rightPaneRef.current;
    if (el && typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => update());
      ro.observe(el);
      return () => ro.disconnect();
    }

    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const [loading, setLoading] = useState(false);
  // True while the components for the selected source are being fetched.
  const [elementsLoading, setElementsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [programmes, setProgrammes] = useState(null);
  // Source browser (left panel): which kind of source is listed, and the dropdown
  // filters per tab ({ [tab]: { [field]: value } }); both persisted to localStorage.
  const [sourceTab, setSourceTab] = useState(() => loadSourceBrowserStateFromStorage().tab);
  const [sourceFiltersByTab, setSourceFiltersByTab] = useState(() => loadSourceBrowserStateFromStorage().filters);
  // Result order per tab ({ [tab]: { key, direction } }; a tab with none uses its
  // default -- see effectiveSort). Set aside (not cleared) while searching, which
  // orders by relevance instead.
  const [sourceSortByTab, setSourceSortByTab] = useState(() => loadSourceBrowserStateFromStorage().sortByTab);
  // The saved programme row a ⋯ menu action (edit/copy/export/archive/restore/
  // delete) applies to.
  const [actionProgramme, setActionProgramme] = useState(null);
  const [rowMenuOpenId, setRowMenuOpenId] = useState(null);
  const [rowMenuPos, setRowMenuPos] = useState(null); // { top, right, openUp } of the open ⋯ menu
  const [rowBusyId, setRowBusyId] = useState(null); // row whose content is being fetched for Add/Move
  // The saved programme row being previewed (View): shown at the top of the results
  // area with its content listed under it, in place of the results. null = results.
  const [previewRow, setPreviewRow] = useState(null);
  const [previewContent, setPreviewContent] = useState(null); // null while loading
  const [previewError, setPreviewError] = useState(null);
  // The results list's scroll position when a preview opened, restored on closing it.
  const resultsScrollTopRef = useRef(null);
  // The slide library (GET /slides/?view=builder) listed on the Slides tab; null until fetched.
  const [selectedElements, setSelectedElements] = useState(null);
  const [exportingSlides, setExportingSlides] = useState(false);
  const [programmeCopied, setProgrammeCopied] = useState(false);
  const [exportingCanvas, setExportingCanvas] = useState(false);
  const [leftSelectedComponents, setLeftSelectedComponents] = useState([]); // shift-click selection from source list
  const [canvasSelectedComponents, setCanvasSelectedComponents] = useState([]); // shift-click selection from canvas
  const [isShiftDown, setIsShiftDown] = useState(false);

  const [programme, setProgramme] = useState(() => loadProgrammeFromStorage());
  const [movingComponent, setMovingComponent] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [activeInsertZone, setActiveInsertZone] = useState(null);
  const [imageModal, setImageModal] = useState({ isOpen: false, image: '', title: '', slideFile: '', slideNumber: '', flags: [] });
  // 0 = hide slides, 1 = default size, 2 = large thumbnails
  const [slideSizeIndex, setSlideSizeIndex] = useState(1);
  const [editModal, setEditModal] = useState({ show: false, type: null, item: null });
  const [editFormData, setEditFormData] = useState({});
  const [duplicateNames, setDuplicateNames] = useState(false);
  const [saveModal, setSaveModal] = useState(false);
  const [saveFormData, setSaveFormData] = useState({ name: '', type: '', public_name: '' });
  // Whether the Save modal's "save as new" form shows Public Name (only when the
  // programme being edited already has one; public names are being phased out).
  const [saveNewShowsPublicName, setSaveNewShowsPublicName] = useState(false);
  const [saveFormMeta, setSaveFormMeta] = useState(emptyProgrammeMeta());
  // Duration / eLearning for a block or module being saved (ComponentDetailFields),
  // in the Save new and Overwrite forms.
  const [saveComponentDetails, setSaveComponentDetails] = useState(componentDetailsOf(null));
  const [overwriteComponentDetails, setOverwriteComponentDetails] = useState(componentDetailsOf(null));
  const [overwriteKey, setOverwriteKey] = useState('');
  const [overwriteName, setOverwriteName] = useState('');
  const [overwriteType, setOverwriteType] = useState('');
  const [overwritePublicName, setOverwritePublicName] = useState('');
  const [overwriteMeta, setOverwriteMeta] = useState(emptyProgrammeMeta());
  const [savingProgramme, setSavingProgramme] = useState(false);
  const [overwriteConfirmModalOpen, setOverwriteConfirmModalOpen] = useState(false);
  const [overwriteConfirmLoading, setOverwriteConfirmLoading] = useState(false);
  const [overwriteConfirmCounts, setOverwriteConfirmCounts] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [deleteProgrammeModalOpen, setDeleteProgrammeModalOpen] = useState(false);
  const [deleteProgrammeAction, setDeleteProgrammeAction] = useState(null); // 'archive' | 'delete'
  const [deleteProgrammeUses, setDeleteProgrammeUses] = useState([]);
  const [deleteProgrammeLoading, setDeleteProgrammeLoading] = useState(false);

  const [editProgrammeModalOpen, setEditProgrammeModalOpen] = useState(false);
  const [editProgrammeName, setEditProgrammeName] = useState('');
  const [editProgrammeType, setEditProgrammeType] = useState('');
  const [editProgrammePublicName, setEditProgrammePublicName] = useState('');
  const [editProgrammeMeta, setEditProgrammeMeta] = useState(emptyProgrammeMeta());
  // Edit Details for a saved block/module: its component's duration / eLearning,
  // loaded from its content (null until loaded; not sent unless loaded).
  const [editProgrammeComponentDetails, setEditProgrammeComponentDetails] = useState(null);
  // The saved template/custom programme's content, for Edit Details' modules summary.
  const [editProgrammeContent, setEditProgrammeContent] = useState(null);
  const [editProgrammeSaving, setEditProgrammeSaving] = useState(false);
  const [editProgrammeError, setEditProgrammeError] = useState(null);
  // Background refresh of `programmes` (see refreshProgrammes).
  const programmesRefreshRef = useRef(null); // the in-flight refresh promise
  const programmesRefreshQueuedRef = useRef(false); // another was requested meanwhile

  const [flagsDropdownOpen, setFlagsDropdownOpen] = useState(false);
  const [selectedFlagNames, setSelectedFlagNames] = useState([]);
  const [flagFilterMatchMode, setFlagFilterMatchMode] = useState('all'); // 'all' (AND) or 'any' (OR)
  const [openFlagFilterGroups, setOpenFlagFilterGroups] = useState({});

  const [leftSelectedComponentsCollapsed, setLeftSelectedComponentsCollapsed] = useState(false);
  const [rightSelectedComponentsCollapsed, setRightSelectedComponentsCollapsed] = useState(false);
  const [canvasMoveSnapshot, setCanvasMoveSnapshot] = useState(null);
  const [canvasSpacerHeight, setCanvasSpacerHeight] = useState(240);
  const [leftPaneWidthPercent, setLeftPaneWidthPercent] = useState(38);
  const [isPaneResizing, setIsPaneResizing] = useState(false);

  const [courses, setCourses] = useState([]);

  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportModalPending, setExportModalPending] = useState(null); // { type: 'programme'|'canvas' }
  const [exportCourseDetails, setExportCourseDetails] = useState({ title: '', month: '', year: '', tutor: '' });
  const [exportSubmitting, setExportSubmitting] = useState(false);
  // Export modal, for a programme with two or more modules: which to export
  // ([{ index, name, slideCount, selected }], `index` = position among the
  // top-level modules), whether to export one file per module, and whether the
  // programme also has slides outside its modules (they go with the next module).
  const [exportModules, setExportModules] = useState([]);
  const [exportModulesLoading, setExportModulesLoading] = useState(false);
  const [exportHasLooseSlides, setExportHasLooseSlides] = useState(false);
  const [exportSeparateFiles, setExportSeparateFiles] = useState(false);
  const exportModalTokenRef = useRef(0); // ignores module lists loaded for an earlier opening
  // Export modal's file name fields, pre-filled from the programme (see
  // _openExportModal): one file "<account> - <programme> <year>.pptx", or per
  // module "<account> Module <N> - <module name> <year>.pptx" (module names are
  // each exportModules entry's `fileName`).
  const [exportFileNames, setExportFileNames] = useState({ account: '', programme: '', year: '' });

  const [undoStates, setUndoStates] = useState([]);
  const [redoStates, setRedoStates] = useState([]);
  const [editingProgramme, setEditingProgramme] = useState(() => loadEditingProgrammeFromStorage());

  const slideSizeMode = slideSizeIndex === 0 ? 'hide' : (slideSizeIndex === 2 ? 'large' : 'default');
  const slidesHidden = slideSizeIndex === 0;
  // Source browser tabs (Slides / Blocks / ... / Archive) as SegmentedControl
  // options, each with an icon: slides the Slide Manager's picture, blocks bricks,
  // parts a small donut, modules stacked layers (a module is a stack of parts),
  // programmes the Tasks clipboard, archive a bin. The block/part/module ones are
  // also on the canvas's "＋ Module / Part / Block" buttons.
  const SOURCE_TAB_ICONS = {
    slides: FaImage,
    blocks: GiBrickWall,
    parts: MdDonutSmall,
    modules: FaLayerGroup,
    programmes: FaClipboardList,
    archive: FaTrashAlt,
  };
  const SOURCE_TAB_OPTIONS = SOURCE_TABS.map((tab) => {
    const Icon = SOURCE_TAB_ICONS[tab.key];
    return {
      value: tab.key,
      label: tab.label,
      icon: Icon ? <Icon size={13} aria-hidden="true" /> : undefined,
      className: `pb-source-tab pb-source-tab--${tab.key}`, // coloured per type (see CSS)
    };
  });

  // Slide size options: hidden, default thumbnails, large thumbnails. Default and
  // Large use the same picture icon at two sizes so they read as a size choice.
  const SLIDE_SIZE_OPTIONS = [
    { value: 0, label: 'Hide slides', icon: <FaEyeSlash size={15} aria-hidden="true" /> },
    { value: 1, label: 'Default size slides', icon: <FaRegImage size={12} aria-hidden="true" /> },
    { value: 2, label: 'Large slides', icon: <FaRegImage size={17} aria-hidden="true" /> },
  ];

  useEffect(() => {
    axios.get('/courses/')
      .then(res => setCourses(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, []);
  
  const canvasRef = useRef(null);
  const layoutRef = useRef(null);
  const componentsListRef = useRef(null);
  // Saved programme content fetched for row Add/Move/Copy, by programme id.
  const rowContentCacheRef = useRef(new Map());
  const flagsFilterContainerRef = useRef(null);
  const flagsFilterPanelRef = useRef(null);
  // The flags filter dropdown stays on screen (shifted/flipped near the edges).
  const flagsFilterPanelStyle = usePopoverPosition(flagsDropdownOpen, flagsFilterContainerRef, flagsFilterPanelRef, { align: 'end' });
  const pendingScrollToEndRef = useRef(false);
  const clearLeftSelectionOnNextInsertRef = useRef(false);
  const clearCanvasSelectionOnNextInsertRef = useRef(false);

  const undoRedoMountedRef = useRef(false);
  const previousProgrammeSnapshotRef = useRef(null);
  const isUndoRedoOperationRef = useRef(false);
  const pendingEditingProgrammeRef = useRef(null);

  const cloneProgrammeState = (value) => {
    try {
      if (typeof structuredClone === 'function') return structuredClone(value);
    } catch {
      // ignore and fall back
    }
    try {
      return JSON.parse(JSON.stringify(value));
    } catch {
      return Array.isArray(value) ? [...value] : value;
    }
  };

  const saveableTypeOrder = ['block', 'part', 'module', 'template', 'custom'];

  const topLevelTypeCompatibility = useMemo(() => {
    const list = Array.isArray(programme) ? programme : [];
    const types = list
      .map(it => it?.type)
      .filter(t => typeof t === 'string' && t.trim().length);

    if (!types.length) {
      return { allowBlock: false, allowPart: false, allowModule: false };
    }

    return {
      allowBlock: types.length === 1 && types[0] === 'block',
      allowPart: types.length === 1 && types[0] === 'part',
      allowModule: types.length === 1 && types[0] === 'module',
    };
  }, [programme]);

  const makeProgrammeKey = (type, name) => `${type}::${name}`;

  // The "Save (overwrite existing)" picker is keyed by id, not name+type: (name, type) is
  // not a uniqueness constraint, so multiple programmes can share both. `overwriteKey` holds
  // the target programme's id (as a string); this resolves it back to the full row.
  const resolveOverwriteProgramme = (key) => {
    if (key === undefined || key === null || key === '') return null;
    return (programmes ?? []).find((p) => p && String(p.id) === String(key)) || null;
  };

  const getSlideTitle = (item) => item?.slide_name || item?.slide_tag || item?.slide || item?.name || 'Element';
  const isUnreviewedSlide = (item) => Number(item?.slide_reviewed) === 0;

  const getSelectedComponentLabel = (item) => {
    if (!item || typeof item !== 'object') return 'Component';
    if (item.type === 'module') return `Module: ${item.name || 'Unnamed'}`;
    if (item.type === 'block') return `Block: ${item.name || 'Unnamed'}`;
    if (item.type === 'part') return `Part: ${item.name || 'Unnamed'}`;
    if (item.type === 'element') return getSlideTitle(item);
    return `${item.type || 'Component'}: ${item.name || getSlideTitle(item)}`;
  };

  const makeSelectionKey = (item) => {
    if (!item || typeof item !== 'object') return 'invalid';
    const type = item.type || 'unknown';
    if (item.slide_tag) return `${type}::tag::${String(item.slide_tag)}`;
    if (item.id !== undefined && item.id !== null) return `${type}::id::${String(item.id)}`;

    const nested = Array.isArray(item.content) ? item.content : [];
    const nestedCount = nested.length;
    const nestedTags = nested
      .filter(c => c && typeof c === 'object' && c.slide_tag)
      .slice(0, 10)
      .map(c => String(c.slide_tag))
      .join('|');

    const raw = [
      type,
      item.name || '',
      item.slide_file || '',
      item.slide_number || '',
      item.slide_name || '',
      item.duration || '',
      nestedCount,
      nestedTags
    ].join('::');

    let hash = 5381;
    for (let i = 0; i < raw.length; i++) {
      hash = ((hash << 5) + hash) + raw.charCodeAt(i);
      hash |= 0;
    }
    return `${type}::h::${(hash >>> 0).toString(36)}`;
  };

  const leftSelectedComponentKeySet = useMemo(() => {
    return new Set((Array.isArray(leftSelectedComponents) ? leftSelectedComponents : []).map(s => s.key));
  }, [leftSelectedComponents]);

  const canvasSelectedComponentKeySet = useMemo(() => {
    return new Set((Array.isArray(canvasSelectedComponents) ? canvasSelectedComponents : []).map(s => s.key));
  }, [canvasSelectedComponents]);

  const programmeComponentKeySet = useMemo(() => {
    const out = new Set();
    const walk = (items) => {
      if (!Array.isArray(items)) return;
      for (const item of items) {
        if (!item || typeof item !== 'object') continue;
        out.add(makeSelectionKey(item));
        if (Array.isArray(item.content)) {
          walk(item.content);
        }
      }
    };

    walk(programme);
    return out;
  }, [programme]);

  const makeCanvasSelectionKey = (item) => {
    if (!item || typeof item !== 'object') return 'invalid-canvas';
    if (item._id !== undefined && item._id !== null) return `canvas::id::${String(item._id)}`;
    return `canvas::fallback::${makeSelectionKey(item)}`;
  };

  const toggleLeftSelectedComponent = (item) => {
    const key = makeSelectionKey(item);
    setLeftSelectedComponents(prev => {
      const arr = Array.isArray(prev) ? prev : [];
      const exists = arr.some(s => s.key === key);
      if (exists) return arr.filter(s => s.key !== key);
      setCanvasSelectedComponents([]);
      return [...arr, { key, item }];
    });
  };

  const toggleCanvasSelectedComponent = (item) => {
    const key = makeCanvasSelectionKey(item);
    setCanvasSelectedComponents(prev => {
      const arr = Array.isArray(prev) ? prev : [];
      const exists = arr.some(s => s.key === key);
      if (exists) return arr.filter(s => s.key !== key);
      setLeftSelectedComponents([]);
      return [...arr, { key, item }];
    });
  };

  const handleLeftComponentContainerClick = (e, item) => {
    if (!e || !e.shiftKey) return;
    const target = e.target;
    if (target && typeof target.closest === 'function') {
      if (target.closest('button, input, textarea, select, a, label')) return;
    }
    e.preventDefault();
    e.stopPropagation();
    toggleLeftSelectedComponent(item);
  };

  const handleCanvasComponentContainerClick = (e, item) => {
    if (!e || !e.shiftKey) return;
    const target = e.target;
    if (target && typeof target.closest === 'function') {
      if (target.closest('button, input, textarea, select, a, label')) return;
    }
    e.preventDefault();
    e.stopPropagation();
    toggleCanvasSelectedComponent(item);
  };

  function handleAddLeftSelectedComponents() {
    if (!leftSelectedComponents || leftSelectedComponents.length === 0) return;
    pendingScrollToEndRef.current = true;
    const items = leftSelectedComponents.map(s => s.item).filter(Boolean);
    setLeftSelectedComponents([]);
    setProgramme(prev => {
      const newProgramme = prev ? [...prev] : [];
      const withIds = items.map(it => assignUniqueIdsRecursively(it));
      newProgramme.push(...withIds);
      return newProgramme;
    });
  }

  function handleMoveLeftSelectedComponents() {
    if (!leftSelectedComponents || leftSelectedComponents.length === 0) return;
    const items = leftSelectedComponents.map(s => s.item).filter(Boolean);
    const itemsWithIds = items.map(it => assignUniqueIdsRecursively(it));

    const hasModule = itemsWithIds.some(element => element.type === 'module');
    const hasPart = itemsWithIds.some(element => element.type === 'part');
    const hasBlock = itemsWithIds.some(element => element.type === 'block');

    const restrictedSet = new Set();
    if (hasModule) {
      restrictedSet.add('inside-module');
      restrictedSet.add('inside-block');
      restrictedSet.add('inside-part');
    }
    if (hasPart) {
      restrictedSet.add('inside-block');
      restrictedSet.add('inside-part');
    }
    if (hasBlock) {
      restrictedSet.add('inside-block');
    }
    const restrictedPlacements = Array.from(restrictedSet);

    setMovingComponent({
      type: 'multi',
      items: itemsWithIds,
      isMultiple: true,
      restrictedPlacements: restrictedPlacements
    });

    // Only clear selection if the move is actually placed (handled in handleInsertMovingComponent)
    clearLeftSelectionOnNextInsertRef.current = true;
  }

  function handleAddCanvasSelectedComponents() {
    if (!canvasSelectedComponents || canvasSelectedComponents.length === 0) return;
    pendingScrollToEndRef.current = true;
    const items = canvasSelectedComponents.map(s => s.item).filter(Boolean);
    setProgramme(prev => {
      const newProgramme = prev ? [...prev] : [];
      const withIds = items.map(it => assignUniqueIdsRecursively(it));
      newProgramme.push(...withIds);
      return newProgramme;
    });
  }

  function handleMoveCanvasSelectedComponents() {
    if (!canvasSelectedComponents || canvasSelectedComponents.length === 0) return;
    const items = canvasSelectedComponents.map(s => s.item).filter(Boolean);
    const idsToMove = new Set(
      items
        .map(item => item?._id)
        .filter(id => id !== undefined && id !== null)
    );
    if (idsToMove.size === 0) return;

    const hasModule = items.some(element => element.type === 'module');
    const hasPart = items.some(element => element.type === 'part');
    const hasBlock = items.some(element => element.type === 'block');

    const restrictedSet = new Set();
    if (hasModule) {
      restrictedSet.add('inside-module');
      restrictedSet.add('inside-block');
      restrictedSet.add('inside-part');
    }
    if (hasPart) {
      restrictedSet.add('inside-block');
      restrictedSet.add('inside-part');
    }
    if (hasBlock) {
      restrictedSet.add('inside-block');
    }
    const restrictedPlacements = Array.from(restrictedSet);

    setMovingComponent({
      type: 'multi',
      items,
      isMultiple: true,
      restrictedPlacements,
      source: 'canvas-selection'
    });
    clearCanvasSelectionOnNextInsertRef.current = true;
  }

  const splitPrefixedOption = (value) => {
    const text = value === null || value === undefined ? '' : String(value);
    const delimiter = ' - ';
    const idx = text.indexOf(delimiter);
    if (idx <= 0) return { prefix: null, suffix: null };
    const prefix = text.slice(0, idx).trim();
    const suffix = text.slice(idx + delimiter.length).trim();
    if (!prefix || !suffix) return { prefix: null, suffix: null };
    return { prefix, suffix };
  };

  const getElementFlagNames = (element) => {
    const list = element?.flags;
    if (!Array.isArray(list)) return [];
    return list
      .map((f) => {
        if (f === null || f === undefined) return '';
        if (typeof f === 'string' || typeof f === 'number') return String(f);
        if (typeof f === 'object') return String(f.name ?? f.label ?? f.value ?? '');
        return '';
      })
      .map(s => s.trim())
      .filter(Boolean);
  };

  // Emoji images shown before a "Geography - <region>" flag; regions not listed
  // (e.g. APAC, IMEA) show text only. "Global" is added by the backend to slides
  // with no geography flag.
  const GEO_FLAG_IMGS = {
    Global: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg/1f310.svg',
    EU:   'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg/1f1ea-1f1fa.svg',
    UK:   'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg/1f1ec-1f1e7.svg',
    USA:  'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg/1f1fa-1f1f8.svg',
  };

  // Flags listed under a slide: Geography, Topic, then Company (see sourceFilters).
  const getRelevantFlags = (item) => slideDisplayFlags(getElementFlagNames(item));

  // `flag` is a label, or { label, tooltip } for a combined entry such as
  // "3 companies" (its hover text lists them).
  const renderFlagTag = (flag, i) => {
    const label = typeof flag === 'string' ? flag : flag?.label;
    const tooltip = typeof flag === 'string' ? null : flag?.tooltip;
    const img = GEO_FLAG_IMGS[label];
    return (
      <span key={i} className="pb-flag-tag">
        {i > 0 && <span className="pb-flag-separator"> | </span>}
        {img && <img src={img} alt="" className="pb-flag-emoji" />}
        {tooltip ? (
          <span className="pb-flag-combined" data-tooltip={tooltip} tabIndex={0}>{label}</span>
        ) : label}
      </span>
    );
  };

  // A saved programme is identified by id when known (name+type is not a uniqueness
  // constraint -- multiple programmes may share both). Falls back to name+type for
  // callers/state that don't carry an id.
  const isSameProgramme = (p, ref) => {
    if (!p || !ref) return false;
    if (ref.id != null) return p.id === ref.id;
    return p.name === ref.name && p.type === ref.type;
  };

  // Flag filter options for the current tab: the slide library's flags on Slides,
  // the block rows' flags (incl. their computed geography) on Blocks.
  const availableFlagOptions = useMemo(() => {
    const out = new Set();
    if (sourceTab === 'blocks') {
      for (const row of rowsForTab(programmes, 'blocks')) {
        for (const name of rowFlagNames(row)) out.add(name);
      }
    } else {
      const walk = (elements) => {
        (Array.isArray(elements) ? elements : []).forEach((el) => {
          if (el?.type === 'element') {
            for (const name of getElementFlagNames(el)) out.add(name);
          }
          if (Array.isArray(el?.content)) walk(el.content);
        });
      };
      walk(selectedElements);
    }
    return Array.from(out).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [sourceTab, programmes, selectedElements]);

  // Saved programme rows on the current (non-slides) tab, after its dropdown
  // filters, flag filter (Blocks) and name search, in display order.
  const sourceTabFilters = sourceFiltersByTab?.[sourceTab] || {};
  const sourceRows = useMemo(() => {
    if (sourceTab === 'slides') return [];
    let rows = applyRowFilters(rowsForTab(programmes, sourceTab), sourceFiltersByTab?.[sourceTab]);
    if (sourceTab === 'blocks') {
      rows = rows.filter((row) => matchesFlagSelection(rowFlagNames(row), selectedFlagNames, flagFilterMatchMode));
    }
    rows = rows.filter((row) => matchesNameSearch(row, searchTerm));
    // Searching sets the chosen sort aside; with no relevance score for rows,
    // they're listed by name meanwhile.
    return sortSourceItems(rows, sourceTab, searchTerm.trim() ? DEFAULT_SORT : sourceSortByTab?.[sourceTab]);
  }, [sourceTab, programmes, sourceFiltersByTab, selectedFlagNames, flagFilterMatchMode, searchTerm, sourceSortByTab]);

  // The previewed row as it is now in the programmes list (so a rename or other
  // Edit Details change shows straight away).
  const previewRowLive = useMemo(() => {
    if (!previewRow) return null;
    if (previewRow.id == null) return previewRow;
    return (programmes || []).find((p) => p && p.id === previewRow.id) || previewRow;
  }, [previewRow, programmes]);
  const sameSourceRow = (a, b) => Boolean(a && b) && (a.id != null || b.id != null
    ? a.id === b.id
    : a.name === b.name && a.type === b.type);

  function openPreview(row) {
    if (!previewRow) resultsScrollTopRef.current = componentsListRef.current?.scrollTop ?? null;
    setLeftSelectedComponents([]);
    setPreviewRow(row);
  }

  // `restoreScroll`: back to the results where they were (the cross / View again);
  // otherwise the results have changed, so they start from the top.
  function closePreview(restoreScroll = true) {
    if (!restoreScroll) resultsScrollTopRef.current = null;
    setLeftSelectedComponents([]);
    setPreviewRow(null);
    setPreviewContent(null);
    setPreviewError(null);
  }

  // Changing tab, search or filters goes back to the (new) results.
  useEffect(() => {
    if (previewRow) closePreview(false);
  }, [sourceTab, searchTerm, sourceFiltersByTab, selectedFlagNames, flagFilterMatchMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // So does the previewed row leaving this tab (deleted, archived or restored).
  useEffect(() => {
    if (!previewRow || programmes === null) return;
    if (!rowsForTab(programmes, sourceTab).some((p) => sameSourceRow(p, previewRow))) closePreview(false);
  }, [programmes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load the previewed row's content; again if it's saved over (modified_at changes).
  const previewContentKey = previewRowLive
    ? `${previewRowLive.id ?? `${previewRowLive.type}::${previewRowLive.name}`}|${previewRowLive.modified_at ?? ''}`
    : null;
  useEffect(() => {
    if (!previewRowLive) return undefined;
    let cancelled = false;
    setPreviewContent(null);
    setPreviewError(null);
    loadRowContent(previewRowLive)
      .then((content) => { if (!cancelled) setPreviewContent(content); })
      .catch((e) => {
        if (!cancelled) setPreviewError(e.response?.data?.message || e.message || 'Failed to load content');
      });
    return () => { cancelled = true; };
  }, [previewContentKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Back on the results: scroll to where they were when the preview opened.
  useEffect(() => {
    if (previewRow || resultsScrollTopRef.current == null) return;
    const top = resultsScrollTopRef.current;
    resultsScrollTopRef.current = null;
    if (componentsListRef.current) componentsListRef.current.scrollTop = top;
  }, [previewRow]);

  // The Month filter only shows once a Year is picked, so clearing the year clears
  // the month too (it would otherwise keep filtering, unseen).
  const setSourceTabFilter = (field, value) => {
    setSourceFiltersByTab((prev) => {
      const next = { ...((prev || {})[sourceTab] || {}), [field]: value };
      if (field === 'year' && !value) next.month = '';
      return { ...(prev || {}), [sourceTab]: next };
    });
  };

  // Close flags dropdown on outside click
  useEffect(() => {
    const handleMouseDown = (e) => {
      if (!flagsDropdownOpen) return;
      if (!flagsFilterContainerRef.current) return;
      if (!flagsFilterContainerRef.current.contains(e.target)) {
        setFlagsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [flagsDropdownOpen]);

  // Close a source row's ⋯ menu on outside click
  useEffect(() => {
    if (rowMenuOpenId == null) return undefined;
    const handleMouseDown = (e) => {
      if (!e.target.closest?.('.pb-source-row-menu-wrap')) setRowMenuOpenId(null);
    };
    // The menu is fixed-positioned, so close it rather than leave it behind on scroll.
    const close = () => setRowMenuOpenId(null);
    document.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [rowMenuOpenId]);

  // Helper function to load programme from localStorage
  function loadProgrammeFromStorage() {
    try {
      const saved = localStorage.getItem('programme-builder-programme');
      if (saved) {
        const parsed = JSON.parse(saved);
        return Array.isArray(parsed) ? parsed : [];
      }
    } catch (error) {
      console.warn('Error loading programme from localStorage:', error);
    }
    return [];
  }

  // Source browser tab + filters from localStorage (unknown/invalid values ignored).
  function loadSourceBrowserStateFromStorage() {
    try {
      const parsed = JSON.parse(localStorage.getItem('programme-builder-source-browser') || 'null');
      const tab = isSourceTab(parsed?.tab) ? parsed.tab : DEFAULT_SOURCE_TAB;
      const filters = parsed?.filters && typeof parsed.filters === 'object' ? parsed.filters : {};
      const sortByTab = {};
      for (const [t, sort] of Object.entries(parsed?.sortByTab || {})) {
        if (isSourceTab(t) && SORT_OPTIONS[sort?.key] && ['asc', 'desc'].includes(sort?.direction)) {
          sortByTab[t] = { key: sort.key, direction: sort.direction };
        }
      }
      return { tab, filters, sortByTab };
    } catch {
      return { tab: DEFAULT_SOURCE_TAB, filters: {}, sortByTab: {} };
    }
  }

  // Helper function to save programme to localStorage
  function saveProgrammeToStorage(programme) {
    try {
      localStorage.setItem('programme-builder-programme', JSON.stringify(programme));
    } catch (error) {
      console.warn('Error saving programme to localStorage:', error);
    }
  }

  // Helper function to load editing programme from localStorage
  function loadEditingProgrammeFromStorage() {
    try {
      const saved = localStorage.getItem('programme-builder-editing-programme');
      if (!saved) return null;
      const parsed = JSON.parse(saved);
      if (parsed && parsed.name) return parsed;
    } catch (error) {
      console.warn('Error loading editing programme from localStorage:', error);
    }
    return null;
  }

  // Helper function to save editing programme to localStorage
  function saveEditingProgrammeToStorage(value) {
    try {
      if (value) {
        localStorage.setItem('programme-builder-editing-programme', JSON.stringify(value));
      } else {
        localStorage.removeItem('programme-builder-editing-programme');
      }
    } catch (error) {
      console.warn('Error saving editing programme to localStorage:', error);
    }
  }

  // Helper function to calculate total duration, slide count, and check for duplicate names
  function calculateTotalDurationAndNames(items) {
    if (!items || !Array.isArray(items)) return { duration: 0, slideCount: 0, moduleNames: [], blockNames: [], hasDuplicates: false };
    
    let total = 0;
    let slides = 0;
    const moduleNames = [];
    const blockNames = [];
    
    for (const item of items) {
      // Collect names by type
      if (item.type === 'module' && item.name) {
        moduleNames.push(item.name);
      } else if (item.type === 'part' && item.name) {
        blockNames.push(item.name);
      } else if (item.type === 'block' && item.name) {
        blockNames.push(item.name);
        
        // Add duration if the item has one
        if (item.duration && !isNaN(parseFloat(item.duration))) {
          total += parseFloat(item.duration);
        }
      } else if (item.type === 'element') {
        // Count slides (elements)
        slides += 1;
      }
      
      // Recursively check content array (for modules and blocks)
      if (item.content && Array.isArray(item.content)) {
        const childResult = calculateTotalDurationAndNames(item.content);
        total += childResult.duration;
        slides += childResult.slideCount;
        moduleNames.push(...childResult.moduleNames);
        blockNames.push(...childResult.blockNames);
      }
    }
    
    // Check for duplicates within each type
    const uniqueModuleNames = new Set(moduleNames);
    const uniqueBlockNames = new Set(blockNames);
    const hasDuplicates = uniqueModuleNames.size !== moduleNames.length || uniqueBlockNames.size !== blockNames.length;
    
    return {
      duration: total,
      slideCount: slides,
      moduleNames,
      blockNames,
      hasDuplicates
    };
  }

  // Helper function to recursively assign unique IDs to all items and their nested content
  function assignUniqueIdsRecursively(item) {
    if (!item || typeof item !== 'object') return item;
    
    // Create a new item with a unique ID
    const itemWithId = {
      ...item,
      _id: Date.now() + Math.random() + Math.random()
    };
    
    // If the item has content array, recursively assign IDs to all content items
    if (itemWithId.content && Array.isArray(itemWithId.content)) {
      itemWithId.content = itemWithId.content.map(contentItem => 
        assignUniqueIdsRecursively(contentItem)
      );
    }
    
    return itemWithId;
  }

  // Calculate total duration and check for duplicates
  const programmeAnalysis = useMemo(() => {
    return calculateTotalDurationAndNames(programme);
  }, [programme]);

  const { blocks: blocksMissingDurationCount, modules: modulesMissingDurationCount } = useMemo(() => {
    const counts = { blocks: 0, modules: 0 };

    const walk = (items) => {
      const list = Array.isArray(items) ? items : [];
      for (const item of list) {
        if (!item || typeof item !== 'object') continue;

        if (item.type === 'block' && isMissingDuration(item)) counts.blocks += 1;
        if (item.type === 'module' && isMissingDuration(item)) counts.modules += 1;

        if (Array.isArray(item.content) && item.content.length) {
          walk(item.content);
        }
      }
    };

    walk(programme);
    return counts;
  }, [programme]);

  // Shown on the warnings badge next to Save; each kind of problem is one warning.
  const programmeWarnings = useMemo(() => {
    const warnings = [];
    if (blocksMissingDurationCount > 0) {
      warnings.push(`${blocksMissingDurationCount} ${blocksMissingDurationCount === 1 ? 'block' : 'blocks'} missing duration`);
    }
    if (modulesMissingDurationCount > 0) {
      warnings.push(`${modulesMissingDurationCount} ${modulesMissingDurationCount === 1 ? 'module' : 'modules'} missing duration`);
    }
    if (duplicateNames) {
      warnings.push('Duplicate module or block names (saving is still allowed)');
    }
    return warnings;
  }, [blocksMissingDurationCount, modulesMissingDurationCount, duplicateNames]);

  const totalDuration = programmeAnalysis.duration;
  const totalSlides = programmeAnalysis.slideCount;

  // Update duplicate names state based on duplicate check
  useEffect(() => {
    setDuplicateNames(programmeAnalysis.hasDuplicates);
  }, [programmeAnalysis.hasDuplicates]);

  // Save programme to localStorage whenever it changes
  useEffect(() => {
    saveProgrammeToStorage(programme);
  }, [programme]);

  // Reset editingProgramme whenever the canvas becomes empty
  useEffect(() => {
    if (Array.isArray(programme) && programme.length === 0) {
      setEditingProgramme(null);
    }
  }, [programme]);

  // Save editing programme to localStorage whenever it changes
  useEffect(() => {
    saveEditingProgrammeToStorage(editingProgramme);
  }, [editingProgramme]);

  // Undo/redo state tracking: push previous canvas state on any non-undo/redo change,
  // and clear redo states when the user makes a new change.
  useEffect(() => {
    const currentSnapshot = cloneProgrammeState(Array.isArray(programme) ? programme : []);

    if (!undoRedoMountedRef.current) {
      undoRedoMountedRef.current = true;
      previousProgrammeSnapshotRef.current = currentSnapshot;
      return;
    }

    if (isUndoRedoOperationRef.current) {
      isUndoRedoOperationRef.current = false;
      previousProgrammeSnapshotRef.current = currentSnapshot;
      return;
    }

    const prevSnapshot = previousProgrammeSnapshotRef.current;
    previousProgrammeSnapshotRef.current = currentSnapshot;

    setUndoStates(prev => [...prev, cloneProgrammeState(prevSnapshot ?? [])]);
    setRedoStates([]);
  }, [programme]);

  const handleUndo = useCallback(() => {
    setUndoStates(prevUndo => {
      if (!prevUndo || prevUndo.length === 0) return prevUndo;

      const nextProgramme = prevUndo[prevUndo.length - 1];
      isUndoRedoOperationRef.current = true;
      setRedoStates(prevRedo => [...(prevRedo || []), cloneProgrammeState(programme)]);
      setProgramme(cloneProgrammeState(nextProgramme));

      return prevUndo.slice(0, -1);
    });
  }, [programme]);

  const handleRedo = useCallback(() => {
    setRedoStates(prevRedo => {
      if (!prevRedo || prevRedo.length === 0) return prevRedo;

      const nextProgramme = prevRedo[prevRedo.length - 1];
      isUndoRedoOperationRef.current = true;
      setUndoStates(prevUndo => [...(prevUndo || []), cloneProgrammeState(programme)]);
      setProgramme(cloneProgrammeState(nextProgramme));

      return prevRedo.slice(0, -1);
    });
  }, [programme]);

  // Keyboard shortcuts: Ctrl+Z / Cmd+Z for undo, Ctrl+Y or Ctrl/Cmd+Shift+Z for redo.
  // Avoid intercepting when typing in inputs/textareas/contentEditable regions.
  useEffect(() => {
    const isEditableTarget = (target) => {
      const el = target;
      if (!el) return false;
      const tag = (el.tagName || '').toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (el.isContentEditable) return true;
      return false;
    };

    const onKeyDown = (e) => {
      const modifier = e.ctrlKey || e.metaKey;
      if (!modifier || e.altKey) return;

      const key = (e.key || '').toLowerCase();
      const isUndo = key === 'z' && !e.shiftKey;
      const isRedo = (key === 'y') || (key === 'z' && e.shiftKey);
      if (!isUndo && !isRedo) return;

      if (isEditableTarget(e.target)) return;

      e.preventDefault();
      if (isUndo) handleUndo();
      else handleRedo();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [handleUndo, handleRedo]);

  useEffect(() => {
    console.log(`ProgrammeBuilder undo/redo sizes: undo=${undoStates.length}, redo=${redoStates.length}`);
  }, [undoStates.length, redoStates.length]);

  // Auto-scroll canvas to the end ONLY for explicit add actions (not moves/placements)
  useEffect(() => {
    if (!pendingScrollToEndRef.current) return;
    pendingScrollToEndRef.current = false;

    const el = canvasRef.current;
    if (!el) return;

    requestAnimationFrame(() => {
      const spacer = el.querySelector('.pb-canvas-scroll-spacer');

      // Scroll so the top of the spacer aligns with the bottom
      // of the visible canvas (i.e. stop before the empty space).
      if (spacer) {
        const canvasRect = el.getBoundingClientRect();
        const spacerRect = spacer.getBoundingClientRect();
        const spacerTop = spacerRect.top - canvasRect.top + el.scrollTop;
        const maxScrollTop = Math.max(0, el.scrollHeight - el.clientHeight);
        const targetScrollTop = Math.max(0, Math.min(spacerTop - el.clientHeight, maxScrollTop));

        // Only scroll down if we're not already past the target.
        // (Avoid snapping the view upward if the user already scrolled further.)
        const epsilon = 2;
        if (el.scrollTop >= targetScrollTop - epsilon) return;

        try {
          el.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
        } catch {
          el.scrollTop = targetScrollTop;
        }
        return;
      }

      // Fallback: old behaviour if spacer isn't present.
      const maxScrollTop = Math.max(0, el.scrollHeight - el.clientHeight);
      if (el.scrollTop >= maxScrollTop - 2) return;
      try {
        el.scrollTo({ top: maxScrollTop, behavior: 'smooth' });
      } catch {
        el.scrollTop = maxScrollTop;
      }
    });
  }, [programme]);

  // Persist the source browser's tab and filters; drop the old dropdown's saved
  // selection, which no longer applies.
  useEffect(() => {
    try {
      localStorage.setItem(
        'programme-builder-source-browser',
        JSON.stringify({ tab: sourceTab, filters: sourceFiltersByTab, sortByTab: sourceSortByTab })
      );
      localStorage.removeItem('programme-builder-selected-programme');
    } catch (error) {
      console.warn('Error saving source browser state to localStorage:', error);
    }
  }, [sourceTab, sourceFiltersByTab, sourceSortByTab]);

  async function loadProgrammes() {
    if (programmes || loading) return;
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/programmes');
      const body = response.data;
      const list = (body?.data ?? []).filter((p) => p && p.type);
      setProgrammes(list);
    } catch (e) {
      setError(e.response?.data?.message || e.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  const saveableProgrammes = useMemo(() => {
    const list = programmes ?? [];
    return list.filter(p => p && p.type && p.type !== 'file' && !isArchivedRow(p));
  }, [programmes]);

  const saveableProgrammeGroups = useMemo(() => {
    const map = { block: [], part: [], module: [], template: [], custom: [] };
    for (const p of saveableProgrammes) {
      if (map[p.type]) map[p.type].push(p);
      else map.custom.push(p);
    }
    for (const t of Object.keys(map)) {
      map[t].sort((a, b) => compareNames(a.name || '', b.name || ''));
    }
    return map;
  }, [saveableProgrammes]);

  // On switching tab: reset search/flags, and load what the tab lists -- the slide
  // library for Slides (once), otherwise a fresh copy of the saved programmes.
  useEffect(() => {
    setSearchTerm('');
    setSelectedFlagNames([]);
    setFlagsDropdownOpen(false);
    setOpenFlagFilterGroups({});
    setFlagFilterMatchMode('all');
    setLeftSelectedComponents([]);
    setRowMenuOpenId(null);

    if (sourceTab !== 'slides') {
      refreshProgrammes();
      return undefined;
    }
    if (selectedElements) return undefined;

    let mounted = true;
    (async () => {
      setElementsLoading(true);
      setError(null);
      try {
        const response = await axios.get('/slides/', { params: { view: 'builder' } });
        if (mounted) setSelectedElements(response.data?.data ?? []);
      } catch (e) {
        if (mounted) {
          setError(e.response?.data?.message || e.message || String(e));
          setSelectedElements([]);
        }
      } finally {
        if (mounted) setElementsLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [sourceTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear shift-click selection when the component source list changes
  useEffect(() => {
    setLeftSelectedComponents([]);
  }, [selectedElements]);

  // Track Shift key so hover/cursor affordance only shows when Shift is held
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Shift') setIsShiftDown(true);
    };
    const handleKeyUp = (e) => {
      if (e.key === 'Shift') setIsShiftDown(false);
    };
    const handleBlur = () => setIsShiftDown(false);

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  // Track mouse position when moving component and update active insert zones
  useEffect(() => {
    if (!movingComponent) {
      setActiveInsertZone(null);
      return;
    }

    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
      
      // Find the closest insertion zone vertically
      if (canvasRef.current) {
        const canvasRect = canvasRef.current.getBoundingClientRect();
        const relativeY = e.clientY - canvasRect.top;
        
        // Check if mouse is within 40px vertically of any insertion point
        const insertionZones = canvasRef.current.querySelectorAll('[data-insert-zone]');
        let closestZone = null;
        let closestDistance = 40; // threshold distance
        
        insertionZones.forEach((zone) => {
          const zoneRect = zone.getBoundingClientRect();
          const zoneY = zoneRect.top - canvasRect.top + (zoneRect.height / 2);
          const distance = Math.abs(relativeY - zoneY);
          
          if (distance < closestDistance) {
            closestDistance = distance;
            closestZone = zone.getAttribute('data-insert-zone');
          }
        });
        
        setActiveInsertZone(closestZone);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setMovingComponent(null);
        setActiveInsertZone(null);
        clearLeftSelectionOnNextInsertRef.current = false;
        clearCanvasSelectionOnNextInsertRef.current = false;
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [movingComponent]);

  // Keep end spacer at half the current canvas height
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;

    const updateSpacerHeight = () => {
      const next = Math.max(0, Math.round(el.clientHeight / 2));
      setCanvasSpacerHeight(next);
    };

    updateSpacerHeight();

    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => updateSpacerHeight());
      resizeObserver.observe(el);
    } else {
      window.addEventListener('resize', updateSpacerHeight);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', updateSpacerHeight);
      }
    };
  }, []);

  // Distance (px) between the cursor and the left pane's width edge when the drag
  // started. The pane's width (flex-basis) excludes its right padding and the
  // divider itself, so mapping the cursor straight to the width made the divider
  // jump right on the first move; keeping this offset holds the divider under the
  // cursor exactly where it was grabbed.
  const paneResizeGrabOffsetRef = useRef(0);

  const handlePaneDividerMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const rect = layoutRef.current?.getBoundingClientRect();
    paneResizeGrabOffsetRef.current = rect?.width
      ? e.clientX - (rect.left + (leftPaneWidthPercent / 100) * rect.width)
      : 0;
    setIsPaneResizing(true);
  }, [leftPaneWidthPercent]);

  useEffect(() => {
    if (!isPaneResizing) return;

    const handleMouseMove = (e) => {
      const layoutElement = layoutRef.current;
      if (!layoutElement) return;

      const rect = layoutElement.getBoundingClientRect();
      if (!rect.width) return;

      const rawPercent = ((e.clientX - paneResizeGrabOffsetRef.current - rect.left) / rect.width) * 100;
      const minLeftPercent = (280 / rect.width) * 100;
      const maxLeftPercent = 100 - (320 / rect.width) * 100;

      const clampMin = Math.max(15, minLeftPercent);
      const clampMax = Math.min(85, maxLeftPercent);
      const nextPercent = clampMin < clampMax
        ? Math.max(clampMin, Math.min(clampMax, rawPercent))
        : 50;

      setLeftPaneWidthPercent(nextPercent);
    };

    const stopResizing = () => {
      setIsPaneResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', stopResizing);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', stopResizing);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isPaneResizing]);

  // Full programme_details row (audience/delivery/access/etc.) for the programme currently
  // loaded onto the canvas, looked up by id first (name+type isn't unique).
  const editingProgrammeInfo = useMemo(() => {
    if (!editingProgramme) return null;
    const list = programmes ?? [];
    if (editingProgramme.id != null) {
      const byId = list.find((p) => p && p.id === editingProgramme.id);
      if (byId) return byId;
    }
    const byNameType = list.find((p) => p && p.type === editingProgramme.type && p.name === editingProgramme.name);
    if (byNameType) return byNameType;
    // `programmes` may not have loaded yet (e.g. right after a page refresh, before the
    // picker/Save modal has been opened) -- editingProgramme itself carries full metadata
    // (audience/delivery/access/etc.), persisted to localStorage, so fall back to it.
    return editingProgramme;
  }, [programmes, editingProgramme]);


  // Only template/custom programmes can have their type changed here (metadata-only edit);
  // block/part/module programmes own a dedicated child component tied to their type.
  const editProgrammeTypeEditable = ['template', 'custom'].includes(actionProgramme?.type);

  const openEditProgrammeModal = (row) => {
    if (!row?.type || !row?.name) return;
    setActionProgramme(row);
    setEditProgrammeError(null);
    setEditProgrammeName(row.name || '');
    setEditProgrammeType(row.type || '');
    setEditProgrammePublicName(row.public_name ?? '');
    setEditProgrammeMeta(metaFromProgramme(row));
    setEditProgrammeComponentDetails(null);
    setEditProgrammeContent(null);
    if (row.type === 'template' || row.type === 'custom') {
      loadRowContent(row)
        .then((content) => setEditProgrammeContent(Array.isArray(content) ? content : []))
        .catch(() => setEditProgrammeContent([]));
    }
    if (row.type === 'block' || row.type === 'module') {
      // Its duration / eLearning live on the block/module itself: its content's
      // top-level item.
      loadRowContent(row)
        .then((content) => {
          const top = (Array.isArray(content) ? content : []).find((item) => item?.type === row.type);
          setEditProgrammeComponentDetails(componentDetailsOf(top));
        })
        .catch(() => setEditProgrammeComponentDetails(componentDetailsOf(null)));
    }
    setEditProgrammeModalOpen(true);
  };

  const closeEditProgrammeModal = () => {
    if (editProgrammeSaving) return;
    setEditProgrammeModalOpen(false);
    setEditProgrammeError(null);
  };

  // Re-fetch the saved programmes list (shared by the Blocks/Parts/Modules/
  // Programmes/Archive tabs) in the background: the current list stays on screen
  // until the new one arrives. A refresh requested while one is in flight runs
  // once more afterwards (it may follow a change the in-flight one predates), and
  // callers awaiting it get that newer result.
  const refreshProgrammes = () => {
    if (programmesRefreshRef.current) {
      programmesRefreshQueuedRef.current = true;
      return programmesRefreshRef.current;
    }
    const run = async () => {
      do {
        programmesRefreshQueuedRef.current = false;
        try {
          const response = await axios.get('/programmes');
          const body = response.data;
          const list = (body?.data ?? []).filter((p) => p && p.type);
          setProgrammes(list);
        } catch (e) {
          setError(e.response?.data?.message || e.message || String(e));
        }
      } while (programmesRefreshQueuedRef.current);
    };
    programmesRefreshRef.current = run().finally(() => {
      programmesRefreshRef.current = null;
    });
    return programmesRefreshRef.current;
  };

  const handleConfirmEditProgramme = async () => {
    if (editProgrammeSaving) return;
    if (!actionProgramme?.type || !actionProgramme?.name) return;

    const nextName = (editProgrammeName || '').trim();
    if (!nextName) {
      setEditProgrammeError('Programme name is required');
      return;
    }

    const nextType = editProgrammeTypeEditable ? (editProgrammeType || actionProgramme.type) : actionProgramme.type;

    setEditProgrammeSaving(true);
    setEditProgrammeError(null);

    try {
      await axios.put(
        '/programme/',
        {
          name: nextName,
          public_name: editProgrammePublicName || null,
          ...(editProgrammeTypeEditable ? { type: nextType } : {}),
          ...metaPayloadForType(nextType, editProgrammeMeta),
          // The block/module's own fields, once loaded (see openEditProgrammeModal).
          ...((nextType === 'block' || nextType === 'module') && editProgrammeComponentDetails
            ? {
              duration: editProgrammeComponentDetails.duration,
              ...(nextType === 'module' ? { bundled_course_ids: editProgrammeComponentDetails.bundled_course_ids } : {}),
            }
            : {}),
        },
        {
          params: programmeParams(actionProgramme),
        }
      );

      setEditProgrammeModalOpen(false);
      rowContentCacheRef.current.delete(actionProgramme.id);
      if (editingProgramme && editingProgramme.type === actionProgramme.type &&
          (editingProgramme.name === actionProgramme.name || editingProgramme.id === actionProgramme.id)) {
        setEditingProgramme(prev => ({
          ...prev,
          name: nextName,
          type: nextType,
          public_name: editProgrammePublicName || null,
          ...metaPayloadForType(nextType, editProgrammeMeta),
        }));
      }
      await refreshProgrammes();
    } catch (e) {
      setEditProgrammeError(e.response?.data?.message || e.response?.data?.error || e.message || 'Failed to update programme');
    } finally {
      setEditProgrammeSaving(false);
    }
  };

  useEffect(() => {
    if (componentsListRef.current) {
      componentsListRef.current.scrollTop = 0;
    }
  }, [searchTerm, sourceTab]);

  // Filter elements based on search term + selected flags
  const filteredElements = useMemo(() => {
    if (!selectedElements) return selectedElements;

    const normalizeText = (s) => s
      .replace(/[\u2018\u2019\u201A\u201B\u02BC\u2032]/g, "'")
      .replace(/[\u201C\u201D\u201E\u201F\u2033]/g, '"');
    const stripPunctuation = (s) => s.replace(/[^\w\s]/g, '').replace(/\s+/g, ' ').trim();

    const searchTerms = normalizeText(searchTerm || '')
      .toLowerCase()
      .split(/[\s,]+/)
      .map(s => s.trim())
      .filter(Boolean);
    const searchTermsStripped = searchTerms.map(stripPunctuation);
    const hasSearch = searchTerms.length > 0;

    const hasFlagFilter = Array.isArray(selectedFlagNames) && selectedFlagNames.length > 0;
    const selectedSet = new Set((selectedFlagNames || []).map(s => String(s)));
    const mode = flagFilterMatchMode === 'any' ? 'any' : 'all';

    if (!hasSearch && !hasFlagFilter) {
      return selectedElements;
    }

    const getSearchFields = (element) => {
      if (!element || typeof element !== 'object') return [];
      const fields = [
        { value: element.name, weight: 1 },
        { value: element.public_name, weight: 1 },
        { value: element.slide_name, weight: 10 },
        { value: element.slide_text, weight: 1 },
        { value: element.slide, weight: 1 }
      ];

      const flagNames = getElementFlagNames(element);
      for (const flag of flagNames) {
        fields.push({ value: flag, weight: 3 });
      }

      return fields
        .map(({ value, weight }) => {
          const text = value === null || value === undefined ? '' : normalizeText(String(value).toLowerCase().trim());
          return { text, stripped: stripPunctuation(text), weight };
        })
        .filter(entry => !!entry.text);
    };

    const buildSearchScore = (element) => {
      if (!hasSearch) return { weightedHits: 0, termHits: 0, sequenceBonus: 0, punctuationBonus: 0, extraChars: 0, totalPosition: 0, textLength: 0 };

      const fields = getSearchFields(element);
      if (!fields.length) return null;

      let weightedHits = 0;
      let termHits = 0;
      let punctuationBonus = 0;
      let extraChars = 0;
      let textLength = 0;
      let totalPosition = 0;

      for (let i = 0; i < searchTerms.length; i++) {
        const term = searchTerms[i];
        const termStripped = searchTermsStripped[i];
        if (!termStripped) continue;

        // Match using punctuation-stripped versions so e.g. "whats" finds "what's"
        const matches = fields
          .map(field => { const pos = field.stripped.indexOf(termStripped); return pos >= 0 ? { ...field, pos } : null; })
          .filter(Boolean);
        if (!matches.length) continue;

        termHits += 1;

        // Punctuation bonus: the exact term (with punctuation) also matches
        if (fields.some(f => f.text.includes(term))) punctuationBonus += 1;

        matches.sort((a, b) => {
          if (a.weight !== b.weight) return b.weight - a.weight;
          if (a.pos !== b.pos) return a.pos - b.pos;
          return a.stripped.length - b.stripped.length;
        });

        const best = matches[0];
        weightedHits += best.weight;
        extraChars += Math.max(0, best.stripped.length - termStripped.length);
        textLength += best.stripped.length;
        totalPosition += best.pos;
      }

      if (termHits === 0) return null;

      // Sequence bonus: all terms appear contiguously in order within a single field
      // (terms may be separated only by whitespace, no other words between them)
      // Uses stripped versions so punctuation is ignored for phrase detection too
      const strippedTermsForSeq = searchTermsStripped.filter(Boolean);
      let sequenceBonus = 0;
      if (strippedTermsForSeq.length > 1) {
        outer: for (const field of fields) {
          let startSearch = 0;
          while (startSearch <= field.stripped.length) {
            const firstPos = field.stripped.indexOf(strippedTermsForSeq[0], startSearch);
            if (firstPos < 0) break;
            let cursor = firstPos + strippedTermsForSeq[0].length;
            let ok = true;
            for (let t = 1; t < strippedTermsForSeq.length; t++) {
              while (cursor < field.stripped.length && /\s/.test(field.stripped[cursor])) cursor++;
              if (!field.stripped.startsWith(strippedTermsForSeq[t], cursor)) { ok = false; break; }
              cursor += strippedTermsForSeq[t].length;
            }
            if (ok) { sequenceBonus = 1; break outer; }
            startSearch = firstPos + 1;
          }
        }
      }

      return { weightedHits, termHits, sequenceBonus, punctuationBonus, extraChars, totalPosition, textLength };
    };

    const compareScores = (a, b) => {
      if (a.sequenceBonus !== b.sequenceBonus) return b.sequenceBonus - a.sequenceBonus;
      if (a.punctuationBonus !== b.punctuationBonus) return b.punctuationBonus - a.punctuationBonus;
      if (a.weightedHits !== b.weightedHits) return b.weightedHits - a.weightedHits;
      if (a.totalPosition !== b.totalPosition) return a.totalPosition - b.totalPosition;
      if (a.termHits !== b.termHits) return b.termHits - a.termHits;
      if (a.extraChars !== b.extraChars) return a.extraChars - b.extraChars;
      return a.textLength - b.textLength;
    };

    const pickBetterScore = (left, right) => {
      if (!left) return right;
      if (!right) return left;
      return compareScores(left, right) <= 0 ? left : right;
    };

    const elementMatchesFlags = (element) => {
      if (!hasFlagFilter) return true;
      if (element?.type !== 'element') return false;

      const names = getElementFlagNames(element);
      if (names.length === 0) return false;

      if (mode === 'any') {
        return names.some(n => selectedSet.has(String(n)));
      }

      const nameSet = new Set(names.map(n => String(n)));
      for (const wanted of selectedSet) {
        if (!nameSet.has(wanted)) return false;
      }
      return true;
    };

    const filterElementsRecursively = (elements) => {
      const entries = (Array.isArray(elements) ? elements : []).reduce((acc, element, index) => {
        if (!element || typeof element !== 'object') return acc;

        const searchScore = buildSearchScore(element);
        const matchesSearch = !hasSearch || !!searchScore;
        const isContainer = element.type === 'module' || element.type === 'block' || element.type === 'part';

        if (hasSearch && isContainer && matchesSearch) {
          acc.push({ element: { ...element, _searchScore: searchScore }, score: searchScore, index });
          return acc;
        }

        const filteredContentEntries = Array.isArray(element.content)
          ? filterElementsRecursively(element.content)
          : [];

        const filteredContent = filteredContentEntries.map(entry => entry.element);
        const bestChildScore = filteredContentEntries.reduce(
          (best, entry) => pickBetterScore(best, entry.score),
          null
        );

        const matchesFlags = elementMatchesFlags(element);

        if ((matchesSearch && matchesFlags) || filteredContent.length > 0) {
          const filteredElement = { ...element };
          if (Array.isArray(element.content)) {
            filteredElement.content = filteredContent;
          }
          filteredElement._searchScore = pickBetterScore(searchScore, bestChildScore);
          acc.push({
            element: filteredElement,
            score: filteredElement._searchScore,
            index
          });
        }

        return acc;
      }, []);

      if (!hasSearch) return entries;

      return entries.sort((a, b) => {
        const scoreA = a.score || { weightedHits: 0, termHits: 0, sequenceBonus: 0, punctuationBonus: 0, extraChars: Number.MAX_SAFE_INTEGER, totalPosition: Number.MAX_SAFE_INTEGER, textLength: Number.MAX_SAFE_INTEGER };
        const scoreB = b.score || { weightedHits: 0, termHits: 0, extraChars: Number.MAX_SAFE_INTEGER, textLength: Number.MAX_SAFE_INTEGER };
        const scoreCmp = compareScores(scoreA, scoreB);
        if (scoreCmp !== 0) return scoreCmp;
        return a.index - b.index;
      });
    };

    return filterElementsRecursively(selectedElements).map(entry => entry.element);
  }, [selectedElements, searchTerm, selectedFlagNames, flagFilterMatchMode]);

  // ── Source results virtualisation ──────────────────────────────────────────
  // Like components/Table.jsx: only the rows scrolled into view (plus `overscan`)
  // are rendered; blank spacers above/below stand in for the rest, so scrolling
  // behaves as if the whole list were there. Matters most on the Slides tab
  // (~1,200 rows with thumbnails), where every re-render otherwise rebuilt them all.
  const isSourceSearchActive = searchTerm.trim() !== '';
  // Slides: relevance order (from filteredElements) while searching, otherwise
  // the chosen sort.
  const sortedSlides = useMemo(
    () => (isSourceSearchActive ? (filteredElements || []) : sortSourceItems(filteredElements, 'slides', sourceSortByTab?.slides)),
    [filteredElements, isSourceSearchActive, sourceSortByTab],
  );
  const sourceListItems = sourceTab === 'slides' ? sortedSlides : sourceRows;
  // Pre-measurement row height guesses; real heights are measured as rows render.
  const estimatedSourceRowHeight = sourceTab !== 'slides'
    ? 64
    : (slideSizeIndex === 0 ? 40 : (slideSizeIndex === 2 ? 260 : 56));
  const sourceListVirtualizer = useVirtualizer({
    count: sourceListItems.length,
    getScrollElement: () => componentsListRef.current,
    estimateSize: () => estimatedSourceRowHeight,
    overscan: 8,
    getItemKey: (index) => {
      const item = sourceListItems[index];
      if (!item) return index;
      if (sourceTab === 'slides') return item.slide_id ?? item.slide_tag ?? index;
      return item.id ?? `${item.type}::${item.name}`;
    },
  });

  // Row heights change with the slide size setting; re-measure from scratch.
  useEffect(() => {
    sourceListVirtualizer.measure();
  }, [slideSizeIndex, sourceTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // The rendered window of `sourceListItems`, wrapped for measurement, between the
  // spacers. `renderItem(item, index)` renders one row.
  function renderVirtualSourceList(renderItem) {
    const virtualItems = sourceListVirtualizer.getVirtualItems();
    const lastIndex = sourceListItems.length - 1;
    const padTop = virtualItems.length ? virtualItems[0].start : 0;
    const padBottom = virtualItems.length
      ? sourceListVirtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end
      : 0;
    return (
      <>
        {padTop > 0 && <div className="pb-virtual-spacer" aria-hidden="true" style={{ height: padTop }} />}
        {virtualItems.map((virtualItem) => (
          <div
            key={virtualItem.key}
            ref={sourceListVirtualizer.measureElement}
            data-index={virtualItem.index}
            className={`pb-virtual-item${virtualItem.index === 0 ? ' is-first' : ''}${virtualItem.index === lastIndex ? ' is-last' : ''}`}
          >
            {renderItem(sourceListItems[virtualItem.index], virtualItem.index)}
          </div>
        ))}
        {padBottom > 0 && <div className="pb-virtual-spacer" aria-hidden="true" style={{ height: padBottom }} />}
      </>
    );
  }

  // Handlers for button actions
  function handleAdd(item) {
    pendingScrollToEndRef.current = true;
    setProgramme(prev => {
      const newProgramme = prev ? [...prev] : [];
      // Add unique ID to the item and all nested content when adding to programme
      const itemWithId = assignUniqueIdsRecursively(item);
      newProgramme.push(itemWithId);
      return newProgramme;
    });
  }

  function handleMove(item) {
    clearLeftSelectionOnNextInsertRef.current = false;
    clearCanvasSelectionOnNextInsertRef.current = false;
    setCanvasMoveSnapshot(null);
    setMovingComponent(item);
  }

  function handleShow(item) {
    const imageSource = getElementImage(item);
    if (!imageSource) return;

    const title = item.slide_name || item.slide_tag || item.slide || item.name || 'Slide Preview';

    setImageModal({
      isOpen: true,
      image: imageSource,
      title: title,
      slideFile: item.slide_file,
      slideNumber: item.slide_number,
      flags: getElementFlagNames(item)
    });
  }

  function closeImageModal() {
    setImageModal({ isOpen: false, image: '', title: '', slideFile: '', slideNumber: '', flags: [] });
  }

  function handleEdit(item) {
    setEditModal({ show: true, type: item.type, item: item });
    setEditFormData({
      name: item.name || '',
      public_name: item.public_name || '',
      duration: item.duration || '',
      bundled_course_ids: item.bundled_course_ids || []
    });
  }

  function closeEditModal() {
    setEditModal({ show: false, type: null, item: null });
    setEditFormData({});
  }

  // Query params addressing one saved programme (id first; name+type for older rows).
  const programmeParams = (row) => ({
    name: row.name,
    type: row.type,
    ...(row.id != null ? { id: row.id } : {}),
  });

  // A saved programme's content tree (cached per id; cleared when it's edited/overwritten).
  async function loadRowContent(row) {
    const cache = rowContentCacheRef.current;
    if (row.id != null && cache.has(row.id)) return cache.get(row.id);
    const response = await axios.get('/programme/', { params: programmeParams(row) });
    const content = Array.isArray(response.data?.data) ? response.data.data : [];
    if (row.id != null) cache.set(row.id, content);
    return content;
  }

  // Fetch a row's content for Add/Move, showing the row as busy meanwhile.
  async function withRowContent(row, action) {
    if (rowBusyId != null) return;
    setRowBusyId(row.id ?? row.name);
    setError(null);
    try {
      const content = await loadRowContent(row);
      if (content.length === 0) {
        alert(`"${row.name}" has no content to add.`);
        return;
      }
      action(content);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load programme content');
    } finally {
      setRowBusyId(null);
    }
  }

  // Add a saved programme's content to the end of the canvas. On an empty canvas the
  // programme becomes the one being edited, so Save offers to overwrite it.
  function addItemsToCanvas(items, sourceRow) {
    if (!items || items.length === 0) return;

    const canvasIsEmpty = !programme || programme.length === 0;
    if (canvasIsEmpty && sourceRow) {
      setEditingProgramme({ ...sourceRow, public_name: sourceRow.public_name ?? null });
    }

    pendingScrollToEndRef.current = true;
    setProgramme(prev => {
      const newProgramme = prev ? [...prev] : [];
      newProgramme.push(...items.map(element => assignUniqueIdsRecursively(element)));
      return newProgramme;
    });
  }

  // Pick up a saved programme's content to place on the canvas (same editing
  // handover as addItemsToCanvas when the canvas is empty).
  function startMoveItems(items, sourceRow) {
    if (!items || items.length === 0) return;

    const canvasIsEmpty = !programme || programme.length === 0;
    if (canvasIsEmpty && sourceRow) {
      pendingEditingProgrammeRef.current = { ...sourceRow, public_name: sourceRow.public_name ?? null };
    }

    clearLeftSelectionOnNextInsertRef.current = false;
    clearCanvasSelectionOnNextInsertRef.current = false;
    setCanvasMoveSnapshot(null);
    
    const elementsWithIds = items.map(element => assignUniqueIdsRecursively(element));
    
    // Analyze content types to determine placement restrictions
    const hasModule = elementsWithIds.some(element => element.type === 'module');
    const hasPart = elementsWithIds.some(element => element.type === 'part');
    const hasBlock = elementsWithIds.some(element => element.type === 'block');
    
    const restrictedSet = new Set();
    if (hasModule) {
      restrictedSet.add('inside-module');
      restrictedSet.add('inside-block');
      restrictedSet.add('inside-part');
    }
    if (hasPart) {
      restrictedSet.add('inside-block');
      restrictedSet.add('inside-part');
    }
    if (hasBlock) {
      restrictedSet.add('inside-block');
    }
    
    setMovingComponent({
      type: 'multi',
      items: elementsWithIds,
      isMultiple: true,
      restrictedPlacements: Array.from(restrictedSet)
    });
  }

  function handleEditSave() {
    if (!editModal.item) return;

    setProgramme(prevProgramme => {
      const unlinkIfLinkedContainer = (item) => {
        if (!item || typeof item !== 'object') return item;
        if ((item.type === 'module' || item.type === 'block' || item.type === 'part') && item.linked) {
          return { ...item, linked: false };
        }
        return item;
      };

      const updateItemRecursively = (items) => {
        if (!Array.isArray(items)) return { items, changed: false };

        let changed = false;
        const next = items.map((item) => {
          if (!item || typeof item !== 'object') return item;

          if (item._id === editModal.item._id) {
            changed = true;
            const updated = {
              ...item,
              name: editFormData.name,
              public_name: editFormData.public_name,
              duration: (editModal.type === 'block' || editModal.type === 'module') ? editFormData.duration : undefined,
              bundled_course_ids: editModal.type === 'module' ? (editFormData.bundled_course_ids || []) : undefined,
            };
            return unlinkIfLinkedContainer(updated);
          }

          if (Array.isArray(item.content)) {
            const child = updateItemRecursively(item.content);
            if (child.changed) {
              changed = true;
              const updated = { ...item, content: child.items };
              return unlinkIfLinkedContainer(updated);
            }
          }

          return item;
        });

        return { items: next, changed };
      };

      return updateItemRecursively(prevProgramme).items;
    });

    closeEditModal();
  }

  function handleSave() {
    loadProgrammes();

    const topLevelItems = Array.isArray(programme) ? programme : [];
    const singleTopLevelItem = topLevelItems.length === 1 ? topLevelItems[0] : null;
    const defaultNewType =
      singleTopLevelItem?.type === 'block' && topLevelTypeCompatibility.allowBlock
        ? 'block'
        : (singleTopLevelItem?.type === 'part' && topLevelTypeCompatibility.allowPart
          ? 'part'
          : (singleTopLevelItem?.type === 'module' && topLevelTypeCompatibility.allowModule
            ? 'module'
            : ''));

    const defaultNewName =
      (singleTopLevelItem?.type === 'block' || singleTopLevelItem?.type === 'part' || singleTopLevelItem?.type === 'module')
        ? (singleTopLevelItem?.name || '')
        : '';

    // If editingProgramme is set, try to find it in the loaded programmes list by id
    // Fall back to editingProgramme itself (which now carries full metadata, persisted to
    // localStorage) when `programmes` hasn't (re-)loaded yet -- e.g. right after a refresh.
    const editingProg = editingProgramme?.id
      ? (programmes ?? []).find(p => p && p.id === editingProgramme.id) ?? editingProgramme
      : null;

    const editingType = editingProgramme?.type;
    const editingTypeCompatible = editingType && editingType !== 'file' && (
      (editingType === 'block' && topLevelTypeCompatibility.allowBlock) ||
      (editingType === 'part' && topLevelTypeCompatibility.allowPart) ||
      (editingType === 'module' && topLevelTypeCompatibility.allowModule) ||
      (editingType !== 'block' && editingType !== 'part' && editingType !== 'module')
    );

    setSaveNewShowsPublicName(Boolean((editingProg?.public_name || '').trim()));
    setSaveFormData({
      name: defaultNewName || editingProgramme?.name || '',
      // The edited programme's type, else block/part/module for a canvas of just
      // one of those, else Template.
      type: (editingTypeCompatible ? editingType : null) || defaultNewType || 'template',
      public_name: editingProg?.public_name || ''
    });
    setSaveFormMeta(editingProg ? metaFromProgramme(editingProg) : emptyProgrammeMeta());
    setSaveComponentDetails(componentDetailsOf(singleTopLevelItem));
    setOverwriteComponentDetails(componentDetailsOf(singleTopLevelItem));

    const overwriteBaseProg = editingProg;
    const overwriteBaseType = overwriteBaseProg?.type;
    const overwriteBaseName = overwriteBaseProg?.name;
    const overwriteBaseId = overwriteBaseProg?.id ?? null;
    const canDefaultOverwrite =
      overwriteBaseType && overwriteBaseType !== 'file' && (
        (overwriteBaseType === 'block' && topLevelTypeCompatibility.allowBlock) ||
        (overwriteBaseType === 'part' && topLevelTypeCompatibility.allowPart) ||
        (overwriteBaseType === 'module' && topLevelTypeCompatibility.allowModule) ||
        (overwriteBaseType !== 'block' && overwriteBaseType !== 'part' && overwriteBaseType !== 'module')
      );

    const defaultOverwriteKey = canDefaultOverwrite && overwriteBaseId != null
      ? String(overwriteBaseId)
      : '';
    setOverwriteKey(defaultOverwriteKey);
    setOverwriteName(overwriteBaseName || '');
    setOverwriteType(
      canDefaultOverwrite && ['block', 'part', 'module', 'template', 'custom'].includes(overwriteBaseType)
        ? overwriteBaseType
        : ''
    );

    const overwriteDescProg = overwriteBaseProg;
    setOverwritePublicName(overwriteDescProg?.public_name || '');
    setOverwriteMeta(overwriteDescProg ? metaFromProgramme(overwriteDescProg) : emptyProgrammeMeta());

    setSaveModal(true);
  }

  function closeSaveModal() {
    setSaveModal(false);
    setSaveFormData({ name: '', type: '', public_name: '' });
    setSaveFormMeta(emptyProgrammeMeta());
    setOverwriteKey('');
    setOverwriteName('');
    setOverwriteType('');
    setOverwritePublicName('');
    setOverwriteMeta(emptyProgrammeMeta());
    setOverwriteConfirmModalOpen(false);
    setOverwriteConfirmCounts(null);
  }

  function countSlidesAndComponents(items) {
    const { slideCount, moduleNames, blockNames } = calculateTotalDurationAndNames(items);
    return { slides: slideCount, components: moduleNames.length + blockNames.length };
  }

  // Fetch the currently saved programme's content and compare its slide/component
  // counts against the canvas's current content, before actually overwriting.
  async function openOverwriteConfirm() {
    if (!overwriteKey) return;
    if (!(overwriteName || '').toString().trim()) return;

    const target = resolveOverwriteProgramme(overwriteKey);
    if (!target?.name || !target?.type) return;

    setOverwriteConfirmLoading(true);
    try {
      const response = await axios.get('/programme/', {
        params: { name: target.name, type: target.type, id: target.id },
      });
      const existingContent = response.data?.data ?? [];

      setOverwriteConfirmCounts({
        existing: countSlidesAndComponents(Array.isArray(existingContent) ? existingContent : []),
        new: countSlidesAndComponents(Array.isArray(programme) ? programme : []),
      });
      setOverwriteConfirmModalOpen(true);
    } catch (e) {
      console.error('Error checking existing programme before overwrite:', e);
      setError(e.response?.data?.message || e.message || String(e));
    } finally {
      setOverwriteConfirmLoading(false);
    }
  }

  async function fetchProgrammeAndReplaceCanvas(name, type, id = null) {
    try {
      const response = await axios.get('/programme/', { params: { name, type, ...(id ? { id } : {}) } });
      const body = response.data;
      const list = body?.data ?? [];
      const listWithIds = (Array.isArray(list) ? list : []).map(item => assignUniqueIdsRecursively(item));

      setMovingComponent(null);
      setCanvasSelectedComponents([]);
      setProgramme(listWithIds);
    } catch (e) {
      console.error('Error fetching saved programme after save:', e);
      setError(e.response?.data?.message || e.message || String(e));
    }
  }

  async function handleOverwriteSubmit() {
    if (!overwriteKey) return;

    const nextName = (overwriteName || '').toString().trim();
    if (!nextName) return;

    const target = resolveOverwriteProgramme(overwriteKey);
    if (!target?.name || !target?.type) return;
    const { name, type } = target;
    const selectedProgrammeId = target.id;

    // The Type dropdown only allows converting between template/custom; block/part/module
    // programmes keep their original type (the dropdown is locked/hidden for those).
    const effectiveType = ['block', 'part', 'module', 'template', 'custom'].includes(type)
      ? (overwriteType || type)
      : type;

    setSavingProgramme(true);
    try {
      const payload = {
        name: nextName,
        type: effectiveType,
        ...(selectedProgrammeId ? { programme_id: selectedProgrammeId } : {}),
        public_name: overwritePublicName || null,
        ...metaPayloadForType(effectiveType, overwriteMeta),
        content: withTopLevelDetails(programme, effectiveType, overwriteComponentDetails),
      };

      const response = await axios.post('/programmes/', payload);
      console.log('Programme overwritten successfully:', response.data);

      const programmeIdFromResponse =
        response.data?.programme_id ?? response.data?.id ?? response.data?.data?.id ?? selectedProgrammeId;

      setProgrammes(prev => {
        const list = prev ?? [];
        return list.map(p => {
          if (p && p.id === selectedProgrammeId) {
            return {
              ...p,
              id: p.id ?? programmeIdFromResponse,
              name: nextName,
              type: effectiveType,
              public_name: overwritePublicName || null,
              ...metaPayloadForType(effectiveType, overwriteMeta),
            };
          }
          return p;
        });
      });

      const overwrittenProgramme = {
        id: programmeIdFromResponse ?? selectedProgrammeId,
        name: nextName,
        type: effectiveType,
        public_name: overwritePublicName || null,
        ...metaPayloadForType(effectiveType, overwriteMeta),
      };

      rowContentCacheRef.current.delete(overwrittenProgramme.id);

      setEditingProgramme(overwrittenProgramme);

      await fetchProgrammeAndReplaceCanvas(nextName, effectiveType, programmeIdFromResponse);

      closeSaveModal();
    } catch (error) {
      console.error('Error overwriting programme:', error.response?.data?.message || error.message);
    } finally {
      setSavingProgramme(false);
    }
  }

  async function handleSaveSubmit() {
    if (!saveFormData.name?.trim() || !saveFormData.type) return;

    setSavingProgramme(true);
    try {
      const payload = {
        name: saveFormData.name,
        type: saveFormData.type,
        public_name: saveFormData.public_name || null,
        ...metaPayloadForType(saveFormData.type, saveFormMeta),
        content: withTopLevelDetails(programme, saveFormData.type, saveComponentDetails),
      };

      const response = await axios.post('/programmes/', payload);
      console.log('Programme saved successfully:', response.data);

      const programmeIdFromResponse =
        response.data?.programme_id ?? response.data?.id ?? response.data?.data?.id;

      setProgrammes(prev => {
        const list = prev ?? [];
        return [
          ...list,
          {
            id: programmeIdFromResponse,
            name: saveFormData.name,
            type: saveFormData.type,
            public_name: saveFormData.public_name || null,
            ...metaPayloadForType(saveFormData.type, saveFormMeta),
          }
        ];
      });

      const newProgramme = {
        id: programmeIdFromResponse,
        name: saveFormData.name,
        type: saveFormData.type,
        public_name: saveFormData.public_name || null,
        ...metaPayloadForType(saveFormData.type, saveFormMeta),
      };

      setEditingProgramme(newProgramme);

      await fetchProgrammeAndReplaceCanvas(saveFormData.name, saveFormData.type, programmeIdFromResponse);

      closeSaveModal();
    } catch (error) {
      console.error('Error saving programme:', error.response?.data?.message || error.message);
    } finally {
      setSavingProgramme(false);
    }
  }

  // Archive a live programme row, or permanently delete an archived one.
  async function handleDeleteProgramme(row) {
    if (!row || row.type === 'file') return;
    setActionProgramme(row);

    const rowIsArchived = isArchivedRow(row);
    const isModulePartBlock = ['module', 'part', 'block'].includes(row.type);
    const action = rowIsArchived ? 'delete' : 'archive';

    if (isModulePartBlock) {
      // Fetch programme_uses then show Modal
      setDeleteProgrammeLoading(true);
      setDeleteProgrammeAction(action);
      setDeleteProgrammeUses([]);
      setDeleteProgrammeModalOpen(true);
      try {
        const res = await axios.get('/programme/', { params: programmeParams(row) });
        setDeleteProgrammeUses(res.data?.programme_uses ?? []);
      } catch (e) {
        // Uses unavailable — still allow the action
        setDeleteProgrammeUses([]);
      } finally {
        setDeleteProgrammeLoading(false);
      }
      return;
    }

    // Non-module/part/block types: keep simple confirm
    const confirmDelete = rowIsArchived
      ? window.confirm(`Are you sure you want to permanently delete the programme "${row.name}"? This action cannot be undone.`)
      : window.confirm(`Are you sure you want to archive the programme "${row.name}"?`);

    if (!confirmDelete) return;

    await executeProgrammeDeleteAction(row);
  }

  async function executeProgrammeDeleteAction(row) {
    if (!row) return;
    try {
      if (isArchivedRow(row)) {
        await axios.delete('/programme/', { params: programmeParams(row) });

        console.log(`Programme deleted: ${row.name}`);

        // Remove entirely from programmes list.
        setProgrammes(prev => (prev ?? []).filter(p => !isSameProgramme(p, row)));
      } else {
        const res = await axios.put('/programme/', { archived: 1 }, { params: programmeParams(row) });

        console.log(`Programme archived: ${row.name}`);

        // Mark archived in programmes list so it moves to the Archive tab.
        const archivedAt = res.data?.data?.archived_at ?? new Date().toISOString().slice(0, 19).replace('T', ' ');
        setProgrammes(prev => (prev ?? []).map(p => (
          isSameProgramme(p, row) ? { ...p, archived_at: archivedAt } : p
        )));
      }
      rowContentCacheRef.current.delete(row.id);
      setActionProgramme(null);
    } catch (error) {
      console.error('Error deleting/archiving programme:', error);
      setError(error.response?.data?.message || error.message || 'Failed to update programme');
    }
  }

  const handleRestoreProgramme = async (row) => {
    if (!row?.type || !row?.name || row.type === 'file' || !isArchivedRow(row)) return;

    try {
      await axios.put('/programme/', { archived: 0 }, { params: programmeParams(row) });

      setProgrammes(prev => (prev ?? []).map(p => (
        isSameProgramme(p, row) ? { ...p, archived_at: null } : p
      )));
    } catch (e) {
      console.error('Error restoring programme:', e);
      setError(e.response?.data?.message || e.message || 'Failed to restore programme');
    }
  };

  // `row` is the saved programme being exported (type 'programme'); unused for 'canvas'.
  // The Export modal's module options for a programme's top-level items (see
  // exportModules): none unless it has at least two modules.
  function exportModuleOptions(items) {
    const list = Array.isArray(items) ? items : [];
    const modules = list.filter((item) => item?.type === 'module');
    if (modules.length < 2) return { modules: [], hasLoose: false };
    const countSlides = (nodes) => (Array.isArray(nodes) ? nodes : []).reduce((n, node) => (
      n + (node?.type === 'element' ? (node.slide_id != null ? 1 : 0) : countSlides(node?.content))
    ), 0);
    return {
      modules: modules.map((module, index) => ({
        index,
        name: (module.public_name || '').trim() || module.name || `Module ${index + 1}`,
        slideCount: countSlides(module.content),
        fileName: (module.public_name || '').trim() || module.name || `Module ${index + 1}`,
        selected: true,
      })),
      hasLoose: list.some((item) => item && item.type !== 'module'),
    };
  }

  function setExportModuleOptions({ modules, hasLoose }) {
    setExportModules(modules);
    setExportHasLooseSlides(hasLoose);
  }

  function _openExportModal(type, row = null) {
    const token = ++exportModalTokenRef.current;
    setExportSeparateFiles(false);
    // File name fields: the exported programme's account, name and year (from the
    // list row, or the programme being edited on the canvas); blank otherwise,
    // except the year, which defaults to this year.
    const nameSource = type === 'programme'
      ? row
      : (editingProgramme
        ? ((programmes ?? []).find((p) => p && p.id === editingProgramme.id) || editingProgramme)
        : null);
    setExportFileNames({
      account: nameSource?.account_name || '',
      programme: nameSource?.name || '',
      year: String(nameSource?.year || new Date().getFullYear()),
    });
    setExportModuleOptions({ modules: [], hasLoose: false });
    setExportModulesLoading(false);
    if (type === 'canvas') {
      setExportModuleOptions(exportModuleOptions(programme));
    } else if (type === 'programme' && row) {
      setExportModulesLoading(true);
      loadRowContent(row)
        .then((content) => {
          if (exportModalTokenRef.current === token) setExportModuleOptions(exportModuleOptions(content));
        })
        .catch(() => {}) // no module list: the whole programme is exported, as before
        .finally(() => {
          if (exportModalTokenRef.current === token) setExportModulesLoading(false);
        });
    }
    const now = new Date();
    const monthName = now.toLocaleString('default', { month: 'long' });
    const year = String(now.getFullYear());
    const title = (type === 'programme' && row?.name)
      ? (row.public_name || row.name)
      : (type === 'canvas' && editingProgramme?.name)
        ? ((programmes ?? []).find(p => p && p.id === editingProgramme.id)?.public_name || editingProgramme.name)
        : '';
    setExportCourseDetails({ title, month: monthName, year, tutor: '' });
    setExportModalPending({ type });
    setExportModalOpen(true);
  }

  // Copy a saved programme's structure (modules/parts/blocks and eLearning) to the
  // clipboard as text + rich text.
  async function handleCopyProgramme(row) {
    if (!row) return;
    let content;
    try {
      content = await loadRowContent(row);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load programme content');
      return;
    }
    if (content.length === 0) return;
    const programmeName = row.public_name || row.name || '';
    const lines = [programmeName, ''];
    const html = [`<p style="margin:0 0 8px"><strong>${escapeHtml(programmeName)}</strong></p>`];
    const ulStyle = 'style="margin:0 0 8px;padding-left:20px"';
    const courseIds = new Set();
    for (const item of content) {
      if (item.type === 'module') {
        const moduleName = item.public_name || item.name || 'Module';
        lines.push(moduleName);
        html.push(`<p style="margin:8px 0 0"><strong>${escapeHtml(moduleName)}</strong></p>`);
        for (const courseId of (item.bundled_course_ids || [])) {
          courseIds.add(courseId);
        }
        const partItems = [];
        for (const part of (item.content || [])) {
          if (part.type === 'part') {
            const partName = part.public_name || part.name || 'Part';
            lines.push(`    ${partName}`);
            const blockItems = [];
            for (const block of (part.content || [])) {
              if (block.type === 'block') {
                const blockName = block.public_name || block.name || 'Block';
                lines.push(`        ${blockName}`);
                blockItems.push(`<li>${escapeHtml(blockName)}</li>`);
              }
            }
            const blockList = blockItems.length ? `<ul ${ulStyle}>${blockItems.join('')}</ul>` : '';
            partItems.push(`<li>${escapeHtml(partName)}${blockList}</li>`);
          } else if (part.type === 'block') {
            const blockName = part.public_name || part.name || 'Block';
            lines.push(`    ${blockName}`);
            partItems.push(`<li>${escapeHtml(blockName)}</li>`);
          }
        }
        if (partItems.length) html.push(`<ul ${ulStyle}>${partItems.join('')}</ul>`);
        lines.push('');
      } else if (item.type === 'part') {
        const partName = item.public_name || item.name || 'Part';
        lines.push(partName);
        html.push(`<p style="margin:8px 0 0">${escapeHtml(partName)}</p>`);
        const blockItems = [];
        for (const block of (item.content || [])) {
          if (block.type === 'block') {
            const blockName = block.public_name || block.name || 'Block';
            lines.push(`    ${blockName}`);
            blockItems.push(`<li>${escapeHtml(blockName)}</li>`);
          }
        }
        if (blockItems.length) html.push(`<ul ${ulStyle}>${blockItems.join('')}</ul>`);
        lines.push('');
      } else if (item.type === 'block') {
        const blockName = item.public_name || item.name || 'Block';
        lines.push(blockName);
        html.push(`<p style="margin:8px 0 0">${escapeHtml(blockName)}</p>`);
      }
    }
    if (courseIds.size > 0) {
      const courseTitles = Array.from(courseIds)
        .map(id => courses.find(c => c.id === id)?.title)
        .filter(Boolean);
      lines.push('E-Learning Courses');
      html.push(`<p style="margin:16px 0 0"><strong>E-Learning Courses</strong></p>`);
      const courseListItems = courseTitles.map(title => `<li>${escapeHtml(title)}</li>`);
      html.push(`<ul ${ulStyle}>${courseListItems.join('')}</ul>`);
      for (const title of courseTitles) {
        lines.push(`    ${title}`);
      }
    }
    const text = lines.join('\n').trimEnd();
    const htmlText = html.join('');
    copyRichText(text, htmlText);
    setProgrammeCopied(row.id ?? row.name);
    setTimeout(() => setProgrammeCopied(false), 1000);
  }

  function handleExportSlides(row) {
    if (exportingSlides || !row || row.type === 'file') return;
    setActionProgramme(row);
    _openExportModal('programme', row);
  }

  async function handleExportCanvas() {
    if (exportingCanvas) return;
    const slideIds = [];
    const walk = (items) => {
      if (!Array.isArray(items)) return;
      for (const item of items) {
        if (!item || typeof item !== 'object') continue;
        if (item.type === 'element') {
          const sid = item.slide_id;
          if (sid !== undefined && sid !== null && String(sid).trim() !== '') slideIds.push(sid);
          continue;
        }
        if (Array.isArray(item.content)) walk(item.content);
      }
    };
    walk(programme);
    if (slideIds.length === 0) {
      alert('No slides found on the canvas to export.');
      return;
    }
    _openExportModal('canvas');
  }

  // Module options for the export request: only when some modules are left out or
  // one file per module is wanted (otherwise the whole programme, as before).
  function exportModuleRequestOptions() {
    if (exportModules.length < 2) return {};
    const selected = exportModules.filter((m) => m.selected).map((m) => m.index);
    if (selected.length === exportModules.length && !exportSeparateFiles) return {};
    return { modules: selected, separate_files: exportSeparateFiles };
  }

  // The file name fields for the export request (see exportFileNames).
  function exportFileNamesRequest() {
    const modules = {};
    if (exportSeparateFiles) {
      for (const m of exportModules) modules[String(m.index)] = m.fileName;
    }
    return { file_names: { ...exportFileNames, modules } };
  }

  const exportSeparate = exportSeparateFiles && exportModules.length > 1;

  const exportNoModulesSelected = exportModules.length > 1 && !exportModules.some((m) => m.selected);

  async function handleExportSubmit() {
    if (exportSubmitting || exportModulesLoading || exportNoModulesSelected) return;
    setExportSubmitting(true);
    setError(null);
    try {
      const course_details = {
        title: exportCourseDetails.title.trim(),
        month: exportCourseDetails.month.trim(),
        year: exportCourseDetails.year.trim(),
        tutor: exportCourseDetails.tutor.trim(),
      };

      if (exportModalPending?.type === 'programme') {
        setExportingSlides(true);
        const programmeId = actionProgramme?.id;
        if (!programmeId) {
          alert('Could not determine programme_id for export.');
          return;
        }
        await axios.post('/jobs/create-slideshow/', {
          programme_id: programmeId,
          course_details,
          ...exportModuleRequestOptions(),
          ...exportFileNamesRequest(),
        });
        setExportModalOpen(false);
        refreshTasks();
      } else if (exportModalPending?.type === 'canvas') {
        setExportingCanvas(true);
        // The saved programme being edited (if any) names the files, as when it's
        // exported from the list.
        await axios.post('/jobs/create-slideshow/', {
          programme: programme,
          course_details,
          ...exportModuleRequestOptions(),
          ...exportFileNamesRequest(),
          ...(editingProgramme?.id != null ? { editing_programme_id: editingProgramme.id } : {}),
        });
        setExportModalOpen(false);
        refreshTasks();
      }
    } catch (error) {
      console.error('Error exporting slides:', error);
      setError(error.response?.data?.message || error.message || 'Failed to export slides');
    } finally {
      setExportSubmitting(false);
      setExportingSlides(false);
      setExportingCanvas(false);
    }
  }

  function getElementImage(item) {
    if (!item) return null;

    const getDeckStem = (value) => {
      if (!value) return '';
      const raw = String(value);
      const fileName = raw.split(/[/\\]/).pop() || raw;
      return fileName.replace(/\.pptx$/i, '');
    };

    const slideNo = Number(item.slide_number);
    const deckStem = getDeckStem(item.slide_file);

    const computedTag = (deckStem && Number.isFinite(slideNo) && slideNo > 0)
      ? `${deckStem}_${slideNo}`
      : null;

    // Legacy fallback (older tag-based naming).
    const fallbackTag = item.slide_tag ? String(item.slide_tag) : null;

    const imageTag = computedTag || fallbackTag;
    if (!imageTag) return null;

    // Always use the API endpoint for slide images.
    // Webpack-bundled assets from ../images won't reflect regenerated slide images
    // until the frontend is rebuilt.
    // ETag + Cache-Control: no-cache on the server ensures stale images are never
    // served: the browser revalidates on every request and only downloads the full
    // image when the file has actually changed.
    return `/api/slide-image/${encodeURIComponent(imageTag)}/`;
  }

  // A new, empty module/part/block, as added by the "＋ Module/Part/Block" buttons.
  const NEW_CONTAINER_NAMES = { module: 'New Module', part: 'New Part', block: 'New Block' };
  function newEmptyContainer(type) {
    return {
      type,
      name: NEW_CONTAINER_NAMES[type],
      public_name: '',
      content: [],
      _id: Date.now() + Math.random(),
    };
  }

  // "＋ Module/Part/Block": append a new empty one to the end of the canvas.
  function handleAddContainer(type) {
    pendingScrollToEndRef.current = true;
    setProgramme(prev => [...(prev || []), newEmptyContainer(type)]);
  }

  // The move-icon button next to each: pick a new empty one up on the cursor to
  // place anywhere on the canvas, like moving an existing component.
  function handleMoveNewContainer(type) {
    handleMove(newEmptyContainer(type));
  }

  function handleDeleteFromProgramme(targetItem) {
    setProgramme(prev => {
      return deleteItemRecursively(prev, targetItem);
    });
  }

  function handleMoveFromProgramme(targetItem) {
    clearLeftSelectionOnNextInsertRef.current = false;
    clearCanvasSelectionOnNextInsertRef.current = false;
    setCanvasMoveSnapshot(null);
    setMovingComponent(targetItem);
    setProgramme(prev => {
      return deleteItemRecursively(prev, targetItem);
    });
  }

  function handleUnlinkFromProgramme(targetItem) {
    const targetId = targetItem?._id;
    if (targetId === undefined || targetId === null) return;

    setProgramme((prev) => {
      const unlinkRecursively = (items) => {
        if (!Array.isArray(items)) return items;

        let changed = false;
        const next = items.map((item) => {
          if (!item || typeof item !== 'object') return item;

          let updated = item;
          if (item?._id === targetId) {
            if (item.linked) {
              updated = { ...item, linked: false };
              changed = true;
            }
          }

          if (Array.isArray(updated?.content)) {
            const child = unlinkRecursively(updated.content);
            if (child !== updated.content) {
              updated = { ...updated, content: child };
              changed = true;
            }
          }

          return updated;
        });

        return changed ? next : items;
      };

      return unlinkRecursively(prev);
    });
  }

  // Recursive function to delete an item from nested structure
  function deleteItemRecursively(items, targetItem) {
    const unlinkIfLinkedContainer = (item) => {
      if (!item || typeof item !== 'object') return item;
      if ((item.type === 'module' || item.type === 'block' || item.type === 'part') && item.linked) {
        return { ...item, linked: false };
      }
      return item;
    };

    const removeRecursively = (list) => {
      if (!Array.isArray(list)) return { items: list, changed: false };

      let changed = false;
      const next = list.reduce((acc, item) => {
        if (!item || typeof item !== 'object') {
          acc.push(item);
          return acc;
        }

        if (item._id === targetItem._id) {
          changed = true;
          return acc;
        }

        if (Array.isArray(item.content)) {
          const child = removeRecursively(item.content);
          if (child.changed) {
            changed = true;
            acc.push(unlinkIfLinkedContainer({ ...item, content: child.items }));
            return acc;
          }
        }

        acc.push(item);
        return acc;
      }, []);

      return { items: next, changed };
    };

    return removeRecursively(items).items;
  }

  function deleteItemsRecursivelyByIds(items, idsToRemove) {
    if (!Array.isArray(items)) return items;
    if (!(idsToRemove instanceof Set) || idsToRemove.size === 0) return items;

    const unlinkIfLinkedContainer = (item) => {
      if (!item || typeof item !== 'object') return item;
      if ((item.type === 'module' || item.type === 'block' || item.type === 'part') && item.linked) {
        return { ...item, linked: false };
      }
      return item;
    };

    const removeRecursively = (list) => {
      if (!Array.isArray(list)) return { items: list, changed: false };

      let changed = false;
      const next = list.reduce((acc, item) => {
        if (!item || typeof item !== 'object') {
          acc.push(item);
          return acc;
        }

        if (idsToRemove.has(item?._id)) {
          changed = true;
          return acc;
        }

        if (Array.isArray(item.content)) {
          const child = removeRecursively(item.content);
          if (child.changed) {
            changed = true;
            acc.push(unlinkIfLinkedContainer({ ...item, content: child.items }));
            return acc;
          }
        }

        acc.push(item);
        return acc;
      }, []);

      return { items: next, changed };
    };

    return removeRecursively(items).items;
  }

  function findItemById(items, targetId) {
    if (!Array.isArray(items) || targetId === undefined || targetId === null) return null;

    for (const item of items) {
      if (item?._id === targetId) return item;
      if (Array.isArray(item?.content)) {
        const match = findItemById(item.content, targetId);
        if (match) return match;
      }
    }

    return null;
  }

  function handleInsertMovingComponent(targetArray, targetIndex, parentItem = null, parentKey = null) {
    if (!movingComponent) return;

    if (movingComponent.source === 'canvas-selection' && movingComponent.isMultiple && Array.isArray(movingComponent.items)) {
      const idsToMove = new Set(
        movingComponent.items
          .map(item => item?._id)
          .filter(id => id !== undefined && id !== null)
      );

      setProgramme(prev => {
        const sourceList = Array.isArray(prev) ? prev : [];
        const movedItems = movingComponent.items.filter(item => idsToMove.has(item?._id));

        let adjustedIndex = targetIndex;
        if (parentItem && parentKey) {
          const parentInPrev = findItemById(sourceList, parentItem._id);
          const originalContainer = Array.isArray(parentInPrev?.[parentKey]) ? parentInPrev[parentKey] : [];
          const movedBefore = originalContainer
            .slice(0, targetIndex)
            .reduce((count, child) => count + (idsToMove.has(child?._id) ? 1 : 0), 0);
          adjustedIndex = Math.max(0, targetIndex - movedBefore);
        } else {
          const movedBefore = sourceList
            .slice(0, targetIndex)
            .reduce((count, child) => count + (idsToMove.has(child?._id) ? 1 : 0), 0);
          adjustedIndex = Math.max(0, targetIndex - movedBefore);
        }

        const withoutMoved = deleteItemsRecursivelyByIds(sourceList, idsToMove);

        if (parentItem && parentKey) {
          return insertMultipleIntoNestedStructure(withoutMoved, parentItem, parentKey, adjustedIndex, movedItems);
        }

        const newProgramme = [...withoutMoved];
        newProgramme.splice(adjustedIndex, 0, ...movedItems);
        return newProgramme;
      });

      if (clearCanvasSelectionOnNextInsertRef.current) {
        setCanvasSelectedComponents([]);
        clearCanvasSelectionOnNextInsertRef.current = false;
      }
      if (clearLeftSelectionOnNextInsertRef.current) {
        setLeftSelectedComponents([]);
        clearLeftSelectionOnNextInsertRef.current = false;
      }

      if (canvasMoveSnapshot) {
        setCanvasMoveSnapshot(null);
      }

      setMovingComponent(null);
      return;
    }
    
    // Handle multiple items
    if (movingComponent.isMultiple && movingComponent.items) {
      const itemsToInsert = movingComponent.items.map(item => 
        item._id ? item : assignUniqueIdsRecursively(item)
      );
      
      if (parentItem && parentKey) {
        // Insert multiple items into nested content (module or block)
        setProgramme(prev => {
          return insertMultipleIntoNestedStructure(prev, parentItem, parentKey, targetIndex, itemsToInsert);
        });
      } else {
        // Insert multiple items into main programme array
        setProgramme(prev => {
          const newProgramme = [...prev];
          newProgramme.splice(targetIndex, 0, ...itemsToInsert);
          return newProgramme;
        });
      }
    } else {
      // Handle single item (existing logic)
      if (parentItem && parentKey) {
        // Insert into nested content (module or block)
        setProgramme(prev => {
          return insertIntoNestedStructure(prev, parentItem, parentKey, targetIndex, movingComponent);
        });
      } else {
        // Insert into main programme array
        setProgramme(prev => {
          const newProgramme = [...prev];
          // Add unique IDs recursively to inserted item and all nested content
          const itemWithId = movingComponent._id ? movingComponent : assignUniqueIdsRecursively(movingComponent);
          newProgramme.splice(targetIndex, 0, itemWithId);
          return newProgramme;
        });
      }
    }

    // If this move originated from either selected components panel, clear only on successful placement
    if (clearLeftSelectionOnNextInsertRef.current) {
      setLeftSelectedComponents([]);
      clearLeftSelectionOnNextInsertRef.current = false;
    }
    if (clearCanvasSelectionOnNextInsertRef.current) {
      setCanvasSelectedComponents([]);
      clearCanvasSelectionOnNextInsertRef.current = false;
    }

    if (canvasMoveSnapshot) {
      setCanvasMoveSnapshot(null);
    }

    if (pendingEditingProgrammeRef.current) {
      setEditingProgramme(pendingEditingProgrammeRef.current);
      pendingEditingProgrammeRef.current = null;
    }

    setMovingComponent(null);
  }

  // Recursive function to insert into nested structure
  function insertIntoNestedStructure(items, targetParent, parentKey, insertIndex, itemToInsert) {
    const unlinkIfLinkedContainer = (item) => {
      if (!item || typeof item !== 'object') return item;
      if ((item.type === 'module' || item.type === 'block' || item.type === 'part') && item.linked) {
        return { ...item, linked: false };
      }
      return item;
    };

    const insertRecursively = (list) => {
      if (!Array.isArray(list)) return { items: list, changed: false };

      let changed = false;
      const next = list.map((item) => {
        if (!item || typeof item !== 'object') return item;

        if (item._id === targetParent._id) {
          changed = true;
          const updatedItem = unlinkIfLinkedContainer({ ...item });
          const currentContent = updatedItem[parentKey] || [];
          const newContent = [...currentContent];
          const itemWithId = itemToInsert._id ? itemToInsert : assignUniqueIdsRecursively(itemToInsert);
          newContent.splice(insertIndex, 0, itemWithId);
          updatedItem[parentKey] = newContent;
          return updatedItem;
        }

        if (Array.isArray(item.content)) {
          const child = insertRecursively(item.content);
          if (child.changed) {
            changed = true;
            return unlinkIfLinkedContainer({ ...item, content: child.items });
          }
        }

        return item;
      });

      return { items: next, changed };
    };

    return insertRecursively(items).items;
  }

  // Recursive function to insert multiple items into nested structure
  function insertMultipleIntoNestedStructure(items, targetParent, parentKey, insertIndex, itemsToInsert) {
    const unlinkIfLinkedContainer = (item) => {
      if (!item || typeof item !== 'object') return item;
      if ((item.type === 'module' || item.type === 'block' || item.type === 'part') && item.linked) {
        return { ...item, linked: false };
      }
      return item;
    };

    const insertRecursively = (list) => {
      if (!Array.isArray(list)) return { items: list, changed: false };

      let changed = false;
      const next = list.map((item) => {
        if (!item || typeof item !== 'object') return item;

        if (item._id === targetParent._id) {
          changed = true;
          const updatedItem = unlinkIfLinkedContainer({ ...item });
          const currentContent = updatedItem[parentKey] || [];
          const newContent = [...currentContent];
          newContent.splice(insertIndex, 0, ...itemsToInsert);
          updatedItem[parentKey] = newContent;
          return updatedItem;
        }

        if (Array.isArray(item.content)) {
          const child = insertRecursively(item.content);
          if (child.changed) {
            changed = true;
            return unlinkIfLinkedContainer({ ...item, content: child.items });
          }
        }

        return item;
      });

      return { items: next, changed };
    };

    return insertRecursively(items).items;
  }

  function renderAddHereButton(targetIndex, parentItem = null, parentKey = null) {
    if (!movingComponent) return null;
    
    // Create unique zone identifier
    const zoneId = parentItem 
      ? `${parentItem._id}-${parentKey}-${targetIndex}`
      : `main-${targetIndex}`;
    
    // Check if this location is valid for the moving component type
    const isValidLocation = () => {
      if (movingComponent.type === 'multi') {
        // Check for restricted placements if they exist
        if (movingComponent.restrictedPlacements) {
          const currentLocation = getCurrentLocation();
          return !movingComponent.restrictedPlacements.includes(currentLocation);
        }
        // If no restrictions, multi-items can go anywhere
        return true;
      } else if (movingComponent.type === 'module') {
        // Modules can only go in the main programme list
        return parentItem === null;
      } else if (movingComponent.type === 'part') {
        // Parts can go in the main programme list or in module content
        return parentItem === null || (parentItem.type === 'module' && parentKey === 'content');
      } else if (movingComponent.type === 'block') {
        // Blocks can go in the main programme list, in module content, or in part content
        return parentItem === null || (parentItem.type === 'module' && parentKey === 'content') || (parentItem.type === 'part' && parentKey === 'content');
      } else {
        // Elements can go anywhere
        return true;
      }
    };
    
    const getCurrentLocation = () => {
      if (parentItem === null) {
        return 'main-programme';
      } else if (parentItem.type === 'module') {
        return 'inside-module';
      } else if (parentItem.type === 'block') {
        return 'inside-block';
      } else if (parentItem.type === 'part') {
        return 'inside-part';
      }
      return 'unknown';
    };
    
    if (!isValidLocation()) {
      return null;
    }
    
    const isActive = activeInsertZone === zoneId;
    
    // Check if canvas is empty (programme has no items) and this is the main programme insertion point
    const isCanvasEmpty = programme && programme.length === 0;
    const isMainProgrammeInsertion = parentItem === null && targetIndex === 0;
    
    // Show button automatically if canvas is empty and this is the main insertion point, or if zone is active
    const shouldShowButton = (isCanvasEmpty && isMainProgrammeInsertion) || isActive;
    
    return (
      <div 
        className={`pb-add-here-container ${isActive ? 'pb-add-here-active' : ''}`}
        data-insert-zone={zoneId}
      >
        {shouldShowButton && (
          <div 
            className="pb-component-preview" 
            onClick={() => handleInsertMovingComponent(null, targetIndex, parentItem, parentKey)}
          >
            {renderComponentPreview(movingComponent)}
          </div>
        )}
      </div>
    );
  }

  // Render a preview of the component being moved
  function renderComponentPreview(item) {
    if (!item || typeof item !== 'object') return null;
    
    // Handle multiple items case
    if (item.type === 'multi' && item.items && Array.isArray(item.items)) {
      return (
        <div className="pb-preview-multi">
          <div className="pb-preview-multi-content">
            Adding {item.items.length} elements
          </div>
        </div>
      );
    }
    
    if (item.type === 'element') {
      // Show element with 50% opacity and dotted border
      const title = getSlideTitle(item);
      const needsReview = isUnreviewedSlide(item);
      return (
        <div className="pb-preview-element">
          <div className="pb-preview-element-content">
            {needsReview && <WarningIcon text="Slide not reviewed" className="pb-warning-symbol-before" />}
            {title}
          </div>
        </div>
      );
    }
    
    if (item.type === 'block') {
      // Show as "Block: name" in a blue box
      return (
        <div className="pb-preview-block">
          <div className="pb-preview-block-content">
            Block: {item.name || 'Unnamed'}
            {item.linked && <IconLink className="pb-match-icon" aria-label="Linked" data-tooltip={linkedComponentTooltip} style={{ marginLeft: 6 }} />}
          </div>
        </div>
      );
    }
    
    if (item.type === 'part') {
      // Show as "Part: name" in a light green box
      return (
        <div className="pb-preview-part">
          <div className="pb-preview-part-content">
            Part: {item.name || 'Unnamed'}
            {item.linked && <IconLink className="pb-match-icon" aria-label="Linked" data-tooltip={linkedComponentTooltip} style={{ marginLeft: 6 }} />}
          </div>
        </div>
      );
    }
    
    if (item.type === 'module') {
      // Show as "Module: name" in a green box
      return (
        <div className="pb-preview-module">
          <div className="pb-preview-module-content">
            Module: {item.name || 'Unnamed'}
            {item.linked && <IconLink className="pb-match-icon" aria-label="Linked" data-tooltip={linkedComponentTooltip} style={{ marginLeft: 6 }} />}
          </div>
        </div>
      );
    }
    
    return null;
  }

  // Recursive component to render structured elements
  function renderElement(item, index) {
    if (!item || typeof item !== 'object') return null;

    const score = (item._searchScore && process.env.REACT_APP_ENVIRONMENT === 'staging') ? item._searchScore : null;
    const scoreBadge = score ? (() => {
      const tooltip = [
        `Weighted hits: ${score.weightedHits}`,
        `Term hits: ${score.termHits}`,
        `Sequence bonus: ${score.sequenceBonus ? 'yes' : 'no'}`,
        `Punctuation matches: ${score.punctuationBonus}`,
        `Total position: ${score.totalPosition}`,
        `Extra chars: ${score.extraChars}`,
        `Text length: ${score.textLength}`,
      ].join('\n');
      const parts = [`W${score.weightedHits}`, `T${score.termHits}`];
      if (score.sequenceBonus) parts.push('S✓');
      if (score.punctuationBonus) parts.push(`Pu${score.punctuationBonus}`);
      parts.push(`Pos${score.totalPosition}`);
      return <span className="pb-score-badge" title={tooltip}>{parts.join(' ')}</span>;
    })() : null;

    const key = `${item.type || 'unknown'}-${index}`;
    const selectionKey = makeSelectionKey(item);
    const isSelected = leftSelectedComponentKeySet.has(selectionKey);
    const appearsInCanvas = programmeComponentKeySet.has(selectionKey);
    
    if (item.type === 'element') {
      // When slides are hidden, don't render element at all
      if (slidesHidden) return null;
      
      const elementImage = getElementImage(item);
      const title = getSlideTitle(item);
      const needsReview = isUnreviewedSlide(item);
      const relevantFlags = getRelevantFlags(item);
      
      return (
        <div
          key={key}
          className={`pb-element${isSelected ? ' pb-component-selected' : ''}`}
          onClick={(e) => handleLeftComponentContainerClick(e, item)}
          title="Shift-click to select"
        >
          <div className="pb-element-content">
            {elementImage ? (
              <img 
                src={elementImage} 
                alt="Element preview" 
                className="pb-element-image-preview pb-clickable-image"
                onClick={(e) => {
                  if (e.shiftKey) {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleLeftSelectedComponent(item);
                    return;
                  }
                  handleShow(item);
                }}
                title="Click to view full image"
              />
            ) : (
              <div className="pb-element-image-placeholder" title="No image available">
                📷
              </div>
            )}
            <span className="pb-element-text">
              <span className="pb-element-title-row">
                {appearsInCanvas && <IconTick className="pb-match-icon" aria-label="In canvas" data-tooltip="In canvas" />}
                {needsReview && <WarningIcon text="Slide not reviewed" className="pb-warning-symbol-before" />}
                {title}
                {scoreBadge}
              </span>
              {relevantFlags.length > 0 && (
                <span className="pb-element-flags">{relevantFlags.map(renderFlagTag)}</span>
              )}
            </span>
            <div className="pb-element-actions">
              <button className="pb-btn pb-btn-add" onClick={() => handleAdd(item)}>＋ Add</button>
              <button className="pb-btn pb-btn-move" onClick={() => handleMove(item)}>
                <IconMove className="pb-btn-icon" aria-hidden="true" /> Move
              </button>
            </div>
          </div>
        </div>
      );
    }
    
    if (item.type === 'block') {
      const durationText = item.duration ? ` (${item.duration} mins)` : '';
      const publicName = (item.public_name ?? '').toString().trim();
      
      return (
        <div
          key={key}
          className={`pb-block${isSelected ? ' pb-component-selected' : ''}`}
          onClick={(e) => handleLeftComponentContainerClick(e, item)}
          title="Shift-click to select"
        >
          <div className="pb-block-header">
            <span>
              {appearsInCanvas && <IconTick className="pb-match-icon" aria-label="In canvas" data-tooltip="In canvas" />}
              <span className="pb-label-prefix">Block:</span> {item.name || 'Unnamed'}
              {isMissingDuration(item) && <MissingDurationIcon type="block" />}
              {item.linked && <IconLink className="pb-match-icon" aria-label="Linked" data-tooltip={linkedComponentTooltip} style={{ marginLeft: 6 }} />}
              {durationText}
              {scoreBadge}
            </span>
            <div className="pb-block-actions">
              <button className="pb-btn pb-btn-add" onClick={() => handleAdd(item)}>＋ Add</button>
              <button className="pb-btn pb-btn-move" onClick={() => handleMove(item)}>
                <IconMove className="pb-btn-icon" aria-hidden="true" /> Move
              </button>
            </div>
          </div>
          {!!publicName && (
            <div className="pb-public-name-strip">"{publicName}"</div>
          )}
          <div className="pb-block-content">
            {item.content && item.content.map((subItem, subIndex) => 
              renderElement(subItem, subIndex)
            )}
          </div>
        </div>
      );
    }
    
    if (item.type === 'part') {
      const publicName = (item.public_name ?? '').toString().trim();
      
      return (
        <div
          key={key}
          className={`pb-part${isSelected ? ' pb-component-selected' : ''}`}
          onClick={(e) => handleLeftComponentContainerClick(e, item)}
          title="Shift-click to select"
        >
          <div className="pb-part-header">
            <span>
              {appearsInCanvas && <IconTick className="pb-match-icon" aria-label="In canvas" data-tooltip="In canvas" />}
              <span className="pb-label-prefix">Part:</span> {item.name || 'Unnamed'}
              {item.linked && <IconLink className="pb-match-icon" aria-label="Linked" data-tooltip={linkedComponentTooltip} style={{ marginLeft: 6 }} />}
              {scoreBadge}
            </span>
            <div className="pb-part-actions">
              <button className="pb-btn pb-btn-add" onClick={() => handleAdd(item)}>＋ Add</button>
              <button className="pb-btn pb-btn-move" onClick={() => handleMove(item)}>
                <IconMove className="pb-btn-icon" aria-hidden="true" /> Move
              </button>
            </div>
          </div>
          {!!publicName && (
            <div className="pb-public-name-strip">"{publicName}"</div>
          )}
          <div className="pb-part-content">
            {item.content && item.content.map((subItem, subIndex) => 
              renderElement(subItem, subIndex)
            )}
          </div>
        </div>
      );
    }
    
    if (item.type === 'module') {
      const moduleDuration = moduleMinutes(item);
      const durationText = moduleDuration ? ` (${formatHours(moduleDuration)} Hr)` : '';
      const publicName = (item.public_name ?? '').toString().trim();
      const courseCount = Array.isArray(item.bundled_course_ids) ? item.bundled_course_ids.length : 0;
      const eLearningLabel = courseCount === 0 ? 'No eLearning' : `${courseCount} eLearning Course${courseCount === 1 ? '' : 's'}`;
      return (
        <div
          key={key}
          className={`pb-module${isSelected ? ' pb-component-selected' : ''}`}
          onClick={(e) => handleLeftComponentContainerClick(e, item)}
          title="Shift-click to select"
        >
          <div className="pb-module-header">
            <span>
              {appearsInCanvas && <IconTick className="pb-match-icon" aria-label="In canvas" data-tooltip="In canvas" />}
              <span className="pb-label-prefix">Module:</span> {item.name || 'Unnamed'}
              {isMissingDuration(item) && <MissingDurationIcon type="module" />}
              {item.linked && <IconLink className="pb-match-icon" aria-label="Linked" data-tooltip={linkedComponentTooltip} style={{ marginLeft: 6 }} />}
              {durationText}
              {scoreBadge}
            </span>
            <div className="pb-module-actions">
              <button className="pb-btn pb-btn-add" onClick={() => handleAdd(item)}>＋ Add</button>
              <button className="pb-btn pb-btn-move" onClick={() => handleMove(item)}>
                <IconMove className="pb-btn-icon" aria-hidden="true" /> Move
              </button>
            </div>
          </div>
          <div className="pb-public-name-strip">
            {publicName ? `"${publicName}" ` : ''}<span className="pb-elearning-count">({eLearningLabel})</span>
          </div>
          <div className="pb-module-content">
            {item.content && item.content.map((subItem, subIndex) => 
              renderElement(subItem, subIndex)
            )}
          </div>
        </div>
      );
    }
    
    // Fallback for unknown structure
    return (
      <div key={key} className="pb-element">
        <div className="pb-element-content">
          {JSON.stringify(item)}
        </div>
        <div className="pb-element-actions">
            <button className="pb-btn pb-btn-add" onClick={() => handleAdd(item)}>＋ Add</button>
            <button className="pb-btn pb-btn-move" onClick={() => handleMove(item)}>
              <IconMove className="pb-btn-icon" aria-hidden="true" /> Move
            </button>
        </div>
      </div>
    );
  }

  // The Flags filter button + dropdown (Slides and Blocks tabs). With flags
  // selected the button lists them (without their "Company - " etc. prefix) and
  // an X button, joined on to its right, clears them.
  function renderFlagsFilter() {
    const selectedFlagLabels = selectedFlagNames.map((name) => splitPrefixedOption(name).suffix || String(name));
    return (
      <div
        className={`table-filter-list pb-flags-filter${selectedFlagNames.length ? ' has-selection' : ''}`}
        style={{ position: 'relative', display: 'flex', alignSelf: 'stretch' }}
        ref={flagsDropdownOpen ? flagsFilterContainerRef : null}
      >
        <button
          type="button"
          className={`table-filter-button pb-flags-filter-button${selectedFlagNames.length ? ' is-active' : ''}`}
          onClick={() => {
            setFlagsDropdownOpen(prev => {
              const next = !prev;
              if (next) setOpenFlagFilterGroups({});
              return next;
            });
          }}
          disabled={availableFlagOptions.length === 0}
          title={
            selectedFlagNames.length
              ? `Filtering by ${selectedFlagLabels.join(', ')} (${flagFilterMatchMode.toUpperCase()})`
              : (availableFlagOptions.length ? 'Filter by flags' : 'No flags available')
          }
        >
          <IconFlag className="btn-inline-icon" aria-hidden="true" />
          <span className="pb-flags-filter-label">
            {selectedFlagNames.length ? selectedFlagLabels.join(', ') : 'Flags'}
          </span>
        </button>
        {selectedFlagNames.length > 0 && (
          <button
            type="button"
            className="table-filter-button pb-flags-filter-clear is-active"
            onClick={() => setSelectedFlagNames([])}
            aria-label="Clear flags"
            title="Clear flags"
          >
            <IconX aria-hidden="true" />
          </button>
        )}

        {flagsDropdownOpen && (
          <div
            ref={flagsFilterPanelRef}
            className="table-filter-dropdown"
            style={{
              ...flagsFilterPanelStyle,
              minWidth: '360px',
              overflowY: 'auto'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <strong style={{ fontSize: '0.9em' }}>Select flags</strong>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  type="button"
                  className="table-filter-dropdown-action"
                  onClick={() => setFlagFilterMatchMode(prev => (prev === 'all' ? 'any' : 'all'))}
                  title="Toggle match mode"
                >
                  {flagFilterMatchMode.toUpperCase()}
                </button>
                <button
                  type="button"
                  className="table-filter-dropdown-action"
                  onClick={() => setSelectedFlagNames([])}
                  disabled={selectedFlagNames.length === 0}
                  title="Clear selection"
                >
                  Clear
                </button>
              </div>
            </div>

            {availableFlagOptions.length === 0 ? (
              <div style={{ fontSize: '0.9em', opacity: 0.7 }}>No options</div>
            ) : (
              <div className="table-filter-list-options" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {(() => {
                  const options = availableFlagOptions;
                  const selected = Array.isArray(selectedFlagNames) ? selectedFlagNames : [];

                  const groupedMap = new Map();
                  const ungrouped = [];
                  (Array.isArray(options) ? options : []).forEach((optionValue) => {
                    const { prefix, suffix } = splitPrefixedOption(optionValue);
                    if (prefix && suffix) {
                      if (!groupedMap.has(prefix)) groupedMap.set(prefix, []);
                      groupedMap.get(prefix).push({ value: optionValue, label: suffix });
                    } else {
                      ungrouped.push({ value: optionValue, label: String(optionValue) });
                    }
                  });

                  const grouped = Array.from(groupedMap.entries())
                    .map(([prefix, items]) => ({
                      prefix,
                      items: items
                        .slice()
                        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
                    }))
                    .sort((a, b) => a.prefix.localeCompare(b.prefix, undefined, { sensitivity: 'base' }));

                  const multiGroups = grouped.filter(g => g.items.length > 1);
                  const singletons = grouped
                    .filter(g => g.items.length === 1)
                    .map(g => ({ value: g.items[0].value, label: String(g.items[0].value) }));

                  const finalUngrouped = [...ungrouped, ...singletons]
                    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

                  const isGroupOpen = (prefix) => {
                    // Default to collapsed
                    return openFlagFilterGroups?.[prefix] === true;
                  };

                  const toggleGroup = (prefix) => {
                    setOpenFlagFilterGroups(prev => {
                      const wasOpen = prev?.[prefix] === true;
                      return {
                        ...(prev || {}),
                        [prefix]: !wasOpen
                      };
                    });
                  };

                  const toggleSelected = (value) => {
                    setSelectedFlagNames(prev => {
                      const arr = Array.isArray(prev) ? prev : [];
                      const exists = arr.includes(value);
                      if (exists) return arr.filter(v => v !== value);
                      return [...arr, value];
                    });
                  };

                  return (
                    <>
                      {multiGroups.map(group => {
                        const groupSelectedCount = group.items.filter(item => selected.includes(item.value)).length;
                        return (
                        <div key={group.prefix} className="table-filter-group">
                          <button
                            type="button"
                            className="table-filter-group-toggle"
                            onClick={() => toggleGroup(group.prefix)}
                            title={isGroupOpen(group.prefix) ? 'Collapse' : 'Expand'}
                          >
                            <span className="table-filter-group-indicator" aria-hidden="true">
                              {isGroupOpen(group.prefix) ? '▾' : '▸'}
                            </span>
                            <span className="table-filter-group-title">
                              {group.prefix}{groupSelectedCount > 0 ? ` (${groupSelectedCount})` : ''}
                            </span>
                          </button>

                          {isGroupOpen(group.prefix) && (
                            <div className="table-filter-group-items">
                              {group.items.map(item => (
                                <label key={item.value} className="table-filter-option">
                                  <Checkbox
                                    size="sm"
                                    checked={selected.includes(item.value)}
                                    onChange={() => toggleSelected(item.value)}
                                  />
                                  <span>{item.label}</span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      );})}

                      {multiGroups.length > 0 && finalUngrouped.length > 0 && (
                        <div className="table-filter-group-separator" />
                      )}

                      {finalUngrouped.map(item => (
                        <label key={item.value} className="table-filter-option">
                          <Checkbox
                            size="sm"
                            checked={selected.includes(item.value)}
                            onChange={() => toggleSelected(item.value)}
                          />
                          <span>{item.label}</span>
                        </label>
                      ))}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // One saved block/part/module/programme row in the source browser, with Add,
  // Move and a ⋯ menu of programme actions.
  function renderSourceRow(row) {
    const key = row.id ?? `${row.type}::${row.name}`;
    const busy = rowBusyId != null && rowBusyId === (row.id ?? row.name);
    const archived = isArchivedRow(row);
    const isProgramme = row.type === 'template' || row.type === 'custom';
    const isContainer = row.type === 'block' || row.type === 'part' || row.type === 'module';

    const meta = [];
    if (sourceTab === 'archive' || isProgramme) meta.push(TYPE_LABELS[row.type] || row.type);
    if (isProgramme) {
      if (row.account_name) meta.push(row.account_name);
      const when = [row.month ? MONTH_LABELS[Number(row.month)] : null, row.year].filter(Boolean).join(' ');
      if (when) meta.push(when);
      for (const field of ['audience', 'delivery', 'access']) {
        if (row[field]) meta.push(row[field]);
      }
      const modules = Number(row.module_count) || 0;
      meta.push(modules ? `${modules} module${modules === 1 ? '' : 's'}` : 'No modules');
    }
    if (isContainer) {
      meta.push(row.slide_count ? `${row.slide_count} slide${row.slide_count === 1 ? '' : 's'}` : 'No slides');
    }

    const menuOpen = rowMenuOpenId === key;
    const closeMenuThen = (fn) => () => {
      setRowMenuOpenId(null);
      fn();
    };
    const copied = programmeCopied === (row.id ?? row.name);
    const previewing = sameSourceRow(previewRowLive, row);

    return (
      <div key={key} className={`pb-source-row pb-source-row--${row.type}${busy ? ' is-busy' : ''}`}>
        <div className="pb-source-row-main">
          <div className="pb-source-row-name">{row.name}</div>
          {meta.length > 0 && <div className="pb-source-row-meta">{meta.join(' · ')}</div>}
          {row.geography && (
            <div className="pb-element-flags pb-source-row-flags">
              {rowDisplayFlags(row).map((flag, i) => renderFlagTag(flag, i))}
            </div>
          )}
          {archived && row.archived_at && (
            <div className="pb-source-row-archived">Archived {formatArchivedDate(row.archived_at)}</div>
          )}
        </div>
        <div className="pb-source-row-actions">
          <button
            type="button"
            className={`pb-btn pb-btn-view${previewing ? ' is-active' : ''}`}
            aria-pressed={previewing}
            onClick={() => (previewing ? closePreview() : openPreview(row))}
            title={previewing ? 'Close the preview' : 'Preview the content'}
          >
            <IconView className="pb-btn-icon" aria-hidden="true" /> View
          </button>
          <button
            type="button"
            className="pb-btn pb-btn-add"
            onClick={() => withRowContent(row, (items) => addItemsToCanvas(items, row))}
            disabled={rowBusyId != null}
          >
            {busy ? 'Loading…' : '＋ Add'}
          </button>
          <button
            type="button"
            className="pb-btn pb-btn-move"
            onClick={() => withRowContent(row, (items) => startMoveItems(items, row))}
            disabled={rowBusyId != null}
          >
            <IconMove className="pb-btn-icon" aria-hidden="true" /> Move
          </button>
          <span className="pb-source-row-menu-wrap">
            <button
              type="button"
              className="pb-btn pb-source-row-menu-button"
              aria-label={`More actions for ${row.name}`}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={(e) => {
                if (menuOpen) {
                  setRowMenuOpenId(null);
                  return;
                }
                // The menu is fixed-positioned so the scrolling list can't clip it;
                // it opens below the button, or above when near the bottom.
                const rect = e.currentTarget.getBoundingClientRect();
                const openUp = rect.bottom + 200 > window.innerHeight;
                setRowMenuPos({
                  top: openUp ? rect.top - 4 : rect.bottom + 4,
                  right: Math.max(8, document.documentElement.clientWidth - rect.right),
                  openUp,
                });
                setRowMenuOpenId(key);
              }}
            >
              <FaEllipsisH aria-hidden="true" />
            </button>
            {menuOpen && rowMenuPos && (
              <div
                className="pb-source-row-menu"
                role="menu"
                style={{
                  top: rowMenuPos.top,
                  right: rowMenuPos.right,
                  transform: rowMenuPos.openUp ? 'translateY(-100%)' : undefined,
                }}
              >
                {archived ? (
                  <>
                    <button type="button" role="menuitem" onClick={closeMenuThen(() => handleRestoreProgramme(row))}>
                      Restore
                    </button>
                    <button type="button" role="menuitem" className="is-danger" onClick={closeMenuThen(() => handleDeleteProgramme(row))}>
                      Delete
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button" role="menuitem" onClick={closeMenuThen(() => openEditProgrammeModal(row))}>
                      <IconEdit className="pb-btn-icon" aria-hidden="true" /> Edit Details
                    </button>
                    <button type="button" role="menuitem" onClick={closeMenuThen(() => handleCopyProgramme(row))}>
                      <IconCopy className="pb-btn-icon" aria-hidden="true" /> {copied ? 'Copied' : 'Copy Summary'}
                    </button>
                    <button type="button" role="menuitem" onClick={closeMenuThen(() => handleExportSlides(row))} disabled={exportingSlides}>
                      <IconExport className="pb-btn-icon" aria-hidden="true" /> Export Slides
                    </button>
                    <button type="button" role="menuitem" className="is-danger" onClick={closeMenuThen(() => handleDeleteProgramme(row))}>
                      <IconClear className="pb-btn-icon" aria-hidden="true" /> Archive
                    </button>
                  </>
                )}
              </div>
            )}
          </span>
        </div>
      </div>
    );
  }

  // Render programme items in the canvas (simpler view without action buttons)
  function renderCanvasElement(item, index) {
    if (!item || typeof item !== 'object') return null;
    
    const key = `canvas-${item.type || 'unknown'}-${index}`;
    const canvasSelectionKey = makeCanvasSelectionKey(item);
    const isCanvasSelected = canvasSelectedComponentKeySet.has(canvasSelectionKey);
    const isCanvasMoving = !!(
      movingComponent?.source === 'canvas-selection' &&
      movingComponent?.isMultiple &&
      Array.isArray(movingComponent?.items) &&
      movingComponent.items.some(m => makeCanvasSelectionKey(m) === canvasSelectionKey)
    );
    
    if (item.type === 'element') {
      // When slides are hidden, don't render element at all
      if (slidesHidden) return null;
      
      const elementImage = getElementImage(item);
      const title = getSlideTitle(item);
      const needsReview = isUnreviewedSlide(item);
      const relevantFlags = getRelevantFlags(item);
      
      return (
        <div
          key={key}
          className={`pb-canvas-element pb-canvas-selectable${isCanvasSelected ? ' pb-canvas-component-selected' : ''}${isCanvasMoving ? ' pb-canvas-selected-grey' : ''}`}
          onClick={(e) => handleCanvasComponentContainerClick(e, item)}
          title="Shift-click to select"
        >
          <div className="pb-canvas-element-content">
            {elementImage ? (
              <img 
                src={elementImage} 
                alt="Element preview" 
                className="pb-canvas-element-image-preview pb-clickable-image"
                onClick={(e) => {
                  if (e.shiftKey) {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleCanvasSelectedComponent(item);
                    return;
                  }
                  handleShow(item);
                }}
                title="Click to view full image"
              />
            ) : (
              <div className="pb-canvas-element-image-placeholder" title="No image available">
                📷
              </div>
            )}
            <span className="pb-canvas-element-text">
              <span className="pb-element-title-row">
                {needsReview && <WarningIcon text="Slide not reviewed" className="pb-warning-symbol-before" />}
                {title}
              </span>
              {relevantFlags.length > 0 && (
                <span className="pb-element-flags">{relevantFlags.map(renderFlagTag)}</span>
              )}
            </span>
            <div className="pb-canvas-element-actions">
              <button className="pb-btn pb-btn-move" onClick={() => handleMoveFromProgramme(item)}>
                <IconMove className="pb-btn-icon" aria-hidden="true" /> Move
              </button>
              <button className="pb-btn pb-btn-delete" onClick={() => handleDeleteFromProgramme(item)}>
                ×
              </button>
            </div>
          </div>
        </div>
      );
    }
    
    if (item.type === 'block') {
      const durationText = item.duration ? ` (${item.duration} mins)` : '';
      const publicName = (item.public_name ?? '').toString().trim();
      
      return (
        <div
          key={key}
          className={`pb-canvas-block pb-canvas-selectable${isCanvasSelected ? ' pb-canvas-component-selected' : ''}${isCanvasMoving ? ' pb-canvas-selected-grey' : ''}`}
          onClick={(e) => handleCanvasComponentContainerClick(e, item)}
          title="Shift-click to select"
        >
          <div className="pb-canvas-block-header">
            <span>
              <span className="pb-label-prefix">Block:</span> {item.name || 'Unnamed'}
              {isMissingDuration(item) && <MissingDurationIcon type="block" />}
              {item.linked && (
                <Tooltip text={linkedComponentTooltip}>
                  <button
                    type="button"
                    className="pb-linked-button"
                    aria-label="Unlink"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleUnlinkFromProgramme(item);
                    }}
                  >
                    <IconLink className="pb-match-icon" aria-hidden="true" />
                    <span className="pb-linked-button-text">unlink</span>
                  </button>
                </Tooltip>
              )}
              {durationText}
            </span>
            <div className="pb-canvas-block-actions">
              <button className="pb-btn pb-btn-edit" onClick={() => handleEdit(item)}><IconEdit className="pb-btn-icon" aria-hidden="true" /> Edit</button>
              <button className="pb-btn pb-btn-move" onClick={() => handleMoveFromProgramme(item)}>
                <IconMove className="pb-btn-icon" aria-hidden="true" /> Move
              </button>
              <button className="pb-btn pb-btn-delete" onClick={() => handleDeleteFromProgramme(item)}>×</button>
            </div>
          </div>
          {!!publicName && (
            <div className="pb-public-name-strip">"{publicName}"</div>
          )}
          <div className="pb-canvas-block-content">
            {item.content && item.content.length > 0 && movingComponent && renderAddHereButton(0, item, 'content')}
            {item.content && item.content.map((subItem, subIndex) => (
              <React.Fragment key={`block-content-${subIndex}`}>
                {renderCanvasElement(subItem, subIndex)}
                {movingComponent && renderAddHereButton(subIndex + 1, item, 'content')}
              </React.Fragment>
            ))}
            {(!item.content || item.content.length === 0) && (
              <div>
                {movingComponent ? (
                  renderAddHereButton(0, item, 'content')
                ) : (
                  (!slidesHidden && <div className="pb-canvas-empty">Empty block</div>)
                )}
              </div>
            )}
          </div>
        </div>
      );
    }
    
    if (item.type === 'part') {
      const publicName = (item.public_name ?? '').toString().trim();
      return (
        <div
          key={key}
          className={`pb-canvas-part pb-canvas-selectable${isCanvasSelected ? ' pb-canvas-component-selected' : ''}${isCanvasMoving ? ' pb-canvas-selected-grey' : ''}`}
          onClick={(e) => handleCanvasComponentContainerClick(e, item)}
          title="Shift-click to select"
        >
          <div className="pb-canvas-part-header">
            <span>
              <span className="pb-label-prefix">Part:</span> {item.name || 'Unnamed'}
              {item.linked && (
                <Tooltip text={linkedComponentTooltip}>
                  <button
                    type="button"
                    className="pb-linked-button"
                    aria-label="Unlink"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleUnlinkFromProgramme(item);
                    }}
                  >
                    <IconLink className="pb-match-icon" aria-hidden="true" />
                    <span className="pb-linked-button-text">unlink</span>
                  </button>
                </Tooltip>
              )}
            </span>
            <div className="pb-canvas-part-actions">
              <button className="pb-btn pb-btn-edit" onClick={() => handleEdit(item)}><IconEdit className="pb-btn-icon" aria-hidden="true" /> Edit</button>
              <button className="pb-btn pb-btn-move" onClick={() => handleMoveFromProgramme(item)}>
                <IconMove className="pb-btn-icon" aria-hidden="true" /> Move
              </button>
              <button className="pb-btn pb-btn-delete" onClick={() => handleDeleteFromProgramme(item)}>×</button>
            </div>
          </div>
          {!!publicName && (
            <div className="pb-public-name-strip">"{publicName}"</div>
          )}
          <div className="pb-canvas-part-content">
            {item.content && item.content.length > 0 && movingComponent && renderAddHereButton(0, item, 'content')}
            {item.content && item.content.map((subItem, subIndex) => (
              <React.Fragment key={`part-content-${subIndex}`}>
                {renderCanvasElement(subItem, subIndex)}
                {movingComponent && renderAddHereButton(subIndex + 1, item, 'content')}
              </React.Fragment>
            ))}
            {(!item.content || item.content.length === 0) && (
              <div>
                {movingComponent ? (
                  renderAddHereButton(0, item, 'content')
                ) : (
                  (!slidesHidden && <div className="pb-canvas-empty">Empty part</div>)
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    if (item.type === 'module') {
      const moduleDuration = moduleMinutes(item);
      const durationText = moduleDuration ? ` (${formatHours(moduleDuration)} Hr)` : '';
      const publicName = (item.public_name ?? '').toString().trim();
      const courseCount = Array.isArray(item.bundled_course_ids) ? item.bundled_course_ids.length : 0;
      const eLearningLabel = courseCount === 0 ? 'No eLearning' : `${courseCount} eLearning Course${courseCount === 1 ? '' : 's'}`;
      return (
        <div
          key={key}
          className={`pb-canvas-module pb-canvas-selectable${isCanvasSelected ? ' pb-canvas-component-selected' : ''}${isCanvasMoving ? ' pb-canvas-selected-grey' : ''}`}
          onClick={(e) => handleCanvasComponentContainerClick(e, item)}
          title="Shift-click to select"
        >
          <div className="pb-canvas-module-header">
            <span>
              <span className="pb-label-prefix">Module:</span> {item.name || 'Unnamed'}
              {isMissingDuration(item) && <MissingDurationIcon type="module" />}
              {item.linked && (
                <Tooltip text={linkedComponentTooltip}>
                  <button
                    type="button"
                    className="pb-linked-button"
                    aria-label="Unlink"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleUnlinkFromProgramme(item);
                    }}
                  >
                    <IconLink className="pb-match-icon" aria-hidden="true" />
                    <span className="pb-linked-button-text">unlink</span>
                  </button>
                </Tooltip>
              )}
              {durationText}
            </span>
            <div className="pb-canvas-module-actions">
              <button className="pb-btn pb-btn-edit" onClick={() => handleEdit(item)}><IconEdit className="pb-btn-icon" aria-hidden="true" /> Edit</button>
              <button className="pb-btn pb-btn-move" onClick={() => handleMoveFromProgramme(item)}>
                <IconMove className="pb-btn-icon" aria-hidden="true" /> Move
              </button>
              <button className="pb-btn pb-btn-delete" onClick={() => handleDeleteFromProgramme(item)}>×</button>
            </div>
          </div>
          <div className="pb-public-name-strip">
            {publicName ? `"${publicName}" ` : ''}<span className="pb-elearning-count">({eLearningLabel})</span>
          </div>
          <div className="pb-canvas-module-content">
            {item.content && item.content.length > 0 && movingComponent && renderAddHereButton(0, item, 'content')}
            {item.content && item.content.map((subItem, subIndex) => (
              <React.Fragment key={`module-content-${subIndex}`}>
                {renderCanvasElement(subItem, subIndex)}
                {movingComponent && renderAddHereButton(subIndex + 1, item, 'content')}
              </React.Fragment>
            ))}
            {(!item.content || item.content.length === 0) && (
              <div>
                {movingComponent ? (
                  renderAddHereButton(0, item, 'content')
                ) : (
                  (!slidesHidden && <div className="pb-canvas-empty">Empty module</div>)
                )}
              </div>
            )}
          </div>
        </div>
      );
    }
    
    return null;
  }

  function renderFloatingComponent() {
    if (!movingComponent) return null;

    // Handle multi-type case
    if (movingComponent.type === 'multi' && movingComponent.items) {
      return (
        <div 
          className="pb-floating-component"
          style={{
            position: 'fixed',
            left: mousePosition.x + 10,
            top: mousePosition.y + 10,
            pointerEvents: 'none',
            zIndex: 1000,
            opacity: 0.7
          }}
        >
          <div className="pb-floating-multi">
            <div className="pb-canvas-element" style={{ background: '#fd7e14', color: 'white', fontWeight: 'bold' }}>
              <div className="pb-canvas-element-content">Moving {movingComponent.items.length} elements</div>
            </div>
          </div>
        </div>
      );
    }

    const componentName = movingComponent.name || movingComponent.slide_name || movingComponent.slide_tag || movingComponent.slide || 'Unnamed';
    
    return (
      <div 
        className="pb-floating-component"
        style={{
          position: 'fixed',
          left: mousePosition.x + 10,
          top: mousePosition.y + 10,
          pointerEvents: 'none',
          zIndex: 1000,
          opacity: 0.5
        }}
      >
        <div className={`pb-floating-${movingComponent.type}`}>
          {movingComponent.type === 'element' && (
            <div className="pb-canvas-element">
              <div className="pb-canvas-element-content">{componentName}</div>
            </div>
          )}
          {movingComponent.type === 'block' && (
            <div className="pb-canvas-block">
              <div className="pb-canvas-block-header">
                <span>
                  Block: {componentName}
                  {movingComponent.linked && <IconLink className="pb-match-icon" aria-label="Linked" data-tooltip={linkedComponentTooltip} style={{ marginLeft: 6 }} />}
                </span>
              </div>
            </div>
          )}
          {movingComponent.type === 'part' && (
            <div className="pb-canvas-part">
              <div className="pb-canvas-part-header">
                <span>
                  Part: {componentName}
                  {movingComponent.linked && <IconLink className="pb-match-icon" aria-label="Linked" data-tooltip={linkedComponentTooltip} style={{ marginLeft: 6 }} />}
                </span>
              </div>
            </div>
          )}
          {movingComponent.type === 'module' && (
            <div className="pb-canvas-module">
              <div className="pb-canvas-module-header">
                <span>
                  Module: {componentName}
                  {movingComponent.linked && <IconLink className="pb-match-icon" aria-label="Linked" data-tooltip={linkedComponentTooltip} style={{ marginLeft: 6 }} />}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`page-layout pb-slide-size-${slideSizeMode}`}>
      <PageTitle><span className="page-title-icon" aria-hidden="true">{typeIcons.programmebuilder}</span>Programme & Slide Deck Builder</PageTitle>
      
      <div className="page-content">
        <div className={`pb-layout${isPaneResizing ? ' pb-layout-resizing' : ''}`} ref={layoutRef}>
        <div className="pb-left" style={{ flexBasis: `${leftPaneWidthPercent}%` }}>
          {/* While previewing (View), "Previewing '…'" and the row sit at the very top of the panel and
              the tabs, filters, sort and search are hidden (not removed, so they're
              as they were when the preview closes), leaving the rest for its content. */}
          <div className={`pb-source-browser${sourceTab !== 'slides' && previewRowLive ? ' pb-source-browser--previewing' : ''}`}>
            {sourceTab !== 'slides' && previewRowLive && (
              <>
                <div className="pb-components-header pb-preview-header">
                  <div className="pb-components-title pb-preview-title" title={previewRowLive.name}>
                    Previewing '{previewRowLive.name}'
                  </div>
                  <button
                    type="button"
                    className="pb-preview-close"
                    onClick={() => closePreview()}
                    aria-label="Close preview"
                    title="Close preview"
                  >
                    <IconX aria-hidden="true" />
                  </button>
                </div>
                <div className="pb-preview-row">{renderSourceRow(previewRowLive)}</div>
              </>
            )}
            <SegmentedControl
              ariaLabel="Source type"
              options={SOURCE_TAB_OPTIONS}
              value={sourceTab}
              onChange={setSourceTab}
              fullWidth
              showLabels
            />

            {(TAB_FILTER_FIELDS[sourceTab] || TABS_WITH_FLAG_FILTER.includes(sourceTab)) && (
              <div className="pb-source-filters">
                {TABS_WITH_FLAG_FILTER.includes(sourceTab) && renderFlagsFilter()}
                {(TAB_FILTER_FIELDS[sourceTab] || []).map(({ field, label }) => {
                  if (field === 'month' && !sourceTabFilters.year) return null; // needs a year first
                  const options = filterOptions(rowsForTab(programmes, sourceTab), field, { monthLabels: MONTH_LABELS });
                  const value = sourceTabFilters[field] || '';
                  return (
                    <select
                      key={field}
                      autoComplete="off"
                      aria-label={label}
                      className={`pb-source-filter${value ? ' is-active' : ''}`}
                      value={value}
                      onChange={(e) => setSourceTabFilter(field, e.target.value)}
                      disabled={options.length === 0 && !value}
                    >
                      <option value="">{label}</option>
                      {options.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  );
                })}
                {Object.values(sourceTabFilters).some(Boolean) && (
                  <button
                    type="button"
                    className="pb-btn pb-source-filters-clear"
                    onClick={() => setSourceFiltersByTab((prev) => ({ ...(prev || {}), [sourceTab]: {} }))}
                  >
                    <IconX className="pb-btn-icon" aria-hidden="true" /> Clear filters
                  </button>
                )}
              </div>
            )}

            {(() => {
              // One sort at a time; clicking the selected one reverses it. While
              // searching, results are in relevance order, so none is selected
              // (the choice comes back when the search is cleared).
              const active = isSourceSearchActive ? null : effectiveSort(sourceTab, sourceSortByTab?.[sourceTab]);
              return (
                <div className="pb-source-sort" role="group" aria-label="Sort results">
                  <span className="pb-source-sort-label">Sort</span>
                  <div className="segmented-control segmented-control--md">
                    {sortKeysForTab(sourceTab).map((key) => {
                      const option = SORT_OPTIONS[key];
                      const isActive = active?.key === key;
                      const direction = isActive ? active.direction : option.defaultDirection;
                      const title = isSourceSearchActive
                        ? 'Clear the search to sort (results are in order of relevance)'
                        : isActive
                          ? `${option.label}: ${option[direction]} (click to reverse)`
                          : `Sort by ${option.label.toLowerCase()}: ${option[direction]}`;
                      return (
                        <button
                          key={key}
                          type="button"
                          className={`segmented-control-option${isActive ? ' is-selected' : ''}`}
                          aria-pressed={isActive}
                          disabled={isSourceSearchActive}
                          title={title}
                          onClick={() => setSourceSortByTab((prev) => ({
                            ...(prev || {}),
                            [sourceTab]: nextSort(effectiveSort(sourceTab, prev?.[sourceTab]), key),
                          }))}
                        >
                          <span className="segmented-control-label">{option.label}</span>
                          {isActive && (direction === 'asc'
                            ? <FaArrowUp className="pb-source-sort-arrow" aria-label={option.asc} />
                            : <FaArrowDown className="pb-source-sort-arrow" aria-label={option.desc} />)}
                        </button>
                      );
                    })}
                  </div>
                  {isSourceSearchActive && <span className="pb-source-sort-note">Sorted by relevance</span>}
                </div>
              );
            })()}

            <div className="pb-search-container">
              <div className="pb-search-input-wrapper">
                <input autoComplete="off"
                  type="text"
                  placeholder={sourceTab === 'slides' ? 'Search slides by name or content...' : 'Search by name...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pb-search-input"
                />
                {searchTerm && (
                  <button
                    className="pb-search-clear"
                    onClick={() => setSearchTerm('')}
                    title="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          </div>

          {(() => {
            const isSlidesTab = sourceTab === 'slides';
            const listLoading = isSlidesTab
              ? elementsLoading
              // These tabs share one list: show the cached one while it refreshes in
              // the background; only the very first load (no list yet) shows Loading.
              : (programmes === null && !error);
            const resultCount = isSlidesTab ? (filteredElements?.length ?? 0) : sourceRows.length;
            const tabLabel = (SOURCE_TABS.find((t) => t.key === sourceTab)?.label || '').toLowerCase();
            const emptyMessage = searchTerm
              ? `No ${tabLabel} found matching "${searchTerm}"`
              : `No ${tabLabel} found`;
            return (
              <div className={`pb-components${isShiftDown && isSlidesTab ? ' pb-shift-select-enabled' : ''}`}>
                {/* While previewing, "Previewing '…'" heads the panel instead (above the row). */}
                {!previewRowLive && (
                  <div className="pb-components-header">
                    <div className="pb-components-title">
                      {resultCount} {resultCount === 1 ? 'result' : 'results'}
                    </div>
                  </div>
                )}

                <div
                  className={`pb-components-list-area${listLoading ? ' pb-components-loading' : ''}`}
                  aria-busy={listLoading}
                >
                  {isSlidesTab ? (
                    filteredElements?.length ? (
                      <div className="pb-components-container pb-scrollable" ref={componentsListRef}>
                        {renderVirtualSourceList((element, index) => renderElement(element, index))}
                      </div>
                    ) : (
                      !listLoading && <div className="pb-muted">{emptyMessage}</div>
                    )
                  ) : previewRowLive ? (
                    <>
                      <div className="pb-components-container pb-scrollable pb-preview-content">
                        {previewError ? (
                          <div className="pb-muted">{previewError}</div>
                        ) : previewContent === null ? (
                          <div className="pb-muted">Loading…</div>
                        ) : previewContent.length === 0 ? (
                          <div className="pb-muted">No content yet</div>
                        ) : (
                          previewContent.map((item, index) => renderElement(item, index))
                        )}
                      </div>
                    </>
                  ) : (
                    sourceRows.length ? (
                      <div className="pb-components-container pb-scrollable" ref={componentsListRef}>
                        {renderVirtualSourceList((row) => renderSourceRow(row))}
                      </div>
                    ) : (
                      !listLoading && <div className="pb-muted">{emptyMessage}</div>
                    )
                  )}
                  {listLoading && (
                    <div className="pb-components-loading-overlay" role="status">
                      <span className="pb-components-loading-message">Loading…</span>
                    </div>
                  )}
                </div>

                {(isSlidesTab || previewRowLive) && leftSelectedComponents.length > 0 && (
            <div className="pb-selected-components-box">
              <div className="pb-selected-components-header">
                <button
                  type="button"
                  className="pb-selected-components-toggle"
                  onClick={() => setLeftSelectedComponentsCollapsed(prev => !prev)}
                  aria-expanded={!leftSelectedComponentsCollapsed}
                  aria-controls="pb-left-selected-components-list"
                >
                  <span className="pb-selected-components-caret" aria-hidden="true">
                    {leftSelectedComponentsCollapsed ? '▸' : '▾'}
                  </span>
                  <span className="pb-selected-components-title">Selected components ({leftSelectedComponents.length})</span>
                </button>
                <div className="pb-selected-components-actions">
                  <button
                    type="button"
                    className="pb-btn pb-btn-clear"
                    onClick={() => setLeftSelectedComponents([])}
                    disabled={leftSelectedComponents.length === 0}
                  >
                    <IconX className="pb-btn-icon" aria-hidden="true" /> Clear
                  </button>
                  <button
                    type="button"
                    className="pb-btn pb-btn-add"
                    onClick={handleAddLeftSelectedComponents}
                    disabled={leftSelectedComponents.length === 0}
                  >
                    ＋ Add
                  </button>
                  <button
                    type="button"
                    className="pb-btn pb-btn-move"
                    onClick={handleMoveLeftSelectedComponents}
                    disabled={leftSelectedComponents.length === 0}
                  >
                    <IconMove className="pb-btn-icon" aria-hidden="true" /> Move
                  </button>
                </div>
              </div>

              <div
                id="pb-left-selected-components-list"
                className="pb-selected-components-list"
                hidden={leftSelectedComponentsCollapsed}
              >
                {leftSelectedComponents.map((s, idx) => (
                  <div
                    key={s.key}
                    className={`pb-selected-components-item pb-selected-components-item--${s?.item?.type || 'unknown'}`}
                  >
                    <span className="pb-selected-components-item-label">{idx + 1}. {getSelectedComponentLabel(s.item)}</span>
                    <button
                      type="button"
                      className="pb-selected-components-remove"
                      onClick={() => setLeftSelectedComponents(prev => (Array.isArray(prev) ? prev : []).filter(x => x.key !== s.key))}
                      title="Remove from selection"
                      aria-label="Remove from selection"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
            )}
              </div>
            );
          })()}

          {error && <div className="pb-error">Error: {error}</div>}
        </div>

        <div
          className="pb-pane-divider"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize left and right panels"
          onMouseDown={handlePaneDividerMouseDown}
        />

        <div className={`pb-right${isShiftDown ? ' pb-canvas-shift-select-enabled' : ''}`} ref={rightPaneRef}>
          <div className="pb-programme-stats-row">

            <div className="pb-slide-size-control">
              <span className="pb-slide-size-label" aria-hidden="true">Slide size</span>
              <SegmentedControl
                ariaLabel="Slide size"
                options={SLIDE_SIZE_OPTIONS}
                value={slideSizeIndex}
                onChange={setSlideSizeIndex}
              />
            </div>
            {editingProgramme && (() => {
              // Details shown after the name: template and custom programmes show
              // access, delivery, account, month/year and audience. Blocks, parts
              // and modules show just the name.
              const info = editingProgrammeInfo || editingProgramme;
              const type = info?.type ?? editingProgramme.type;
              const details = [];
              if (type === 'template' || type === 'custom') {
                details.push(codedText(info?.access), deliveryLabel(info?.delivery));
                const month = info?.month ? MONTH_LABELS[Number(info.month)] : null;
                details.push(
                  info?.account_name || null,
                  [month, info?.year].filter(Boolean).join(' ') || null,
                  (typeof info?.audience === 'string' && info.audience.trim()) || null,
                );
              }
              return (
                <div className="pb-editing-box">
                  <span className="pb-editing-box-label">Editing:</span>
                  <div className="pb-editing-box-info">
                    <strong className="pb-editing-box-name">{editingProgramme.name}</strong>
                    {details.filter(Boolean).map((detail) => (
                      <span key={detail} className="pb-editing-box-meta">{detail}</span>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
          <div className="pb-programme-actions">
            {[['module', 'Module', FaLayerGroup], ['part', 'Part', MdDonutSmall], ['block', 'Block', GiBrickWall]].map(([type, label, Icon]) => (
              <span key={type} className="pb-add-pair">
                <button className={`pb-btn pb-btn-add pb-btn-add-${type}`} onClick={() => handleAddContainer(type)}>
                  <Icon className="pb-btn-icon" aria-hidden="true" />＋ {label}
                </button>
                <button
                  type="button"
                  className="pb-btn pb-btn-move pb-btn-move-new"
                  onClick={() => handleMoveNewContainer(type)}
                  title={`Place a new ${label.toLowerCase()} anywhere on the canvas`}
                  aria-label={`Place a new ${label.toLowerCase()}`}
                >
                  <IconMove aria-hidden="true" />
                </button>
              </span>
            ))}
            <button
              type="button"
              className="pb-btn pb-btn-secondary"
              onClick={handleUndo}
              disabled={undoStates.length === 0}
              title="Undo"
            >
              <IconUndo className="pb-btn-icon" aria-hidden="true" />{!isRightPaneNarrow && <> Undo</>}
            </button>
            <button
              type="button"
              className="pb-btn pb-btn-secondary"
              onClick={handleRedo}
              disabled={redoStates.length === 0}
              title="Redo"
            >
              <IconRedo className="pb-btn-icon" aria-hidden="true" />{!isRightPaneNarrow && <> Redo</>}
            </button>
            <button
              className='pb-btn pb-btn-delete'
              onClick={() => { setProgramme([]); setEditingProgramme(null); }}
              disabled={!programme || programme.length === 0}
            >
              × Clear
            </button>

            <div className="pb-programme-actions-right">
              <div className="pb-total-slides">
                <div><IconClock aria-hidden="true" style={{ marginRight: '4px', verticalAlign: 'middle' }} />{totalDuration} mins</div>
                <div>{totalSlides} Slides</div>
              </div>
              <button 
                className="pb-btn pb-btn-save"
                onClick={handleSave}
                disabled={!programme || programme.length === 0}
              >
                <IconSave className="pb-btn-icon" aria-hidden="true" />{!isRightPaneNarrow && <> Save</>}
              </button>
              {programmeWarnings.length > 0 && (
                <Tooltip text={programmeWarnings.join('\n')} variant="warning">
                  <span
                    className="pb-warnings-badge"
                    tabIndex={0}
                    aria-label={`${programmeWarnings.length} ${programmeWarnings.length === 1 ? 'warning' : 'warnings'}: ${programmeWarnings.join('; ')}`}
                  >
                    <FaExclamationTriangle className="pb-warning-symbol" aria-hidden="true" />
                    {programmeWarnings.length}
                  </span>
                </Tooltip>
              )}

              <button
                type="button"
                className="pb-btn pb-btn-add-all"
                onClick={handleExportCanvas}
                disabled={exportingCanvas || totalSlides === 0}
                title="Export the current canvas as a slideshow"
              >
                <IconExport className="pb-btn-icon" aria-hidden="true" />{!isRightPaneNarrow && <> Export Slides</>}
              </button>
            </div>
          </div>
          
          <div className="pb-canvas pb-scrollable" ref={canvasRef}>
            {programme && programme.length > 0 ? (
              <div className="pb-canvas-content">
                {movingComponent && renderAddHereButton(0)}
                {programme.map((item, index) => (
                  <React.Fragment key={`programme-${index}`}>
                    {renderCanvasElement(item, index)}
                    {movingComponent && renderAddHereButton(index + 1)}
                  </React.Fragment>
                ))}
                <div className="pb-canvas-scroll-spacer" style={{ height: `${canvasSpacerHeight}px` }} aria-hidden="true" />
              </div>
            ) : (
              <div className="pb-canvas-placeholder">
                {movingComponent && (
                  <div className="pb-canvas-add-here-empty">
                    {renderAddHereButton(0)}
                  </div>
                )}
              </div>
            )}
          </div>

          {canvasSelectedComponents.length > 0 && (
          <div className="pb-selected-components-box pb-right-selected-components-box">
            <div className="pb-selected-components-header">
              <button
                type="button"
                className="pb-selected-components-toggle"
                onClick={() => setRightSelectedComponentsCollapsed(prev => !prev)}
                aria-expanded={!rightSelectedComponentsCollapsed}
                aria-controls="pb-right-selected-components-list"
              >
                <span className="pb-selected-components-caret" aria-hidden="true">
                  {rightSelectedComponentsCollapsed ? '▸' : '▾'}
                </span>
                <span className="pb-selected-components-title">Selected components ({canvasSelectedComponents.length})</span>
              </button>
              <div className="pb-selected-components-actions">
                <button
                  type="button"
                  className="pb-btn pb-btn-clear"
                  onClick={() => setCanvasSelectedComponents([])}
                  disabled={canvasSelectedComponents.length === 0}
                >
                  <IconX className="pb-btn-icon" aria-hidden="true" /> Clear
                </button>
                <button
                  type="button"
                  className="pb-btn pb-btn-add"
                  onClick={handleAddCanvasSelectedComponents}
                  disabled={canvasSelectedComponents.length === 0}
                >
                  ＋ Add
                </button>
                <button
                  type="button"
                  className="pb-btn pb-btn-move"
                  onClick={handleMoveCanvasSelectedComponents}
                  disabled={canvasSelectedComponents.length === 0}
                >
                  <IconMove className="pb-btn-icon" aria-hidden="true" /> Move
                </button>
              </div>
            </div>

            <div
              id="pb-right-selected-components-list"
              className="pb-selected-components-list"
              hidden={rightSelectedComponentsCollapsed}
            >
              {canvasSelectedComponents.map((s, idx) => (
                <div
                  key={s.key}
                  className={`pb-selected-components-item pb-selected-components-item--${s?.item?.type || 'unknown'}`}
                >
                  <span className="pb-selected-components-item-label">{idx + 1}. {getSelectedComponentLabel(s.item)}</span>
                  <button
                    type="button"
                    className="pb-selected-components-remove"
                    onClick={() => setCanvasSelectedComponents(prev => (Array.isArray(prev) ? prev : []).filter(x => x.key !== s.key))}
                    title="Remove from selection"
                    aria-label="Remove from selection"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
          )}
        </div>
      </div>
      
      {/* Debug sections - commented out but preserved for future use
      <div className="pb-debug">
      <div className="pb-debug-title">Programme State (Debug)</div>
      <pre className="pb-debug-content">
        {JSON.stringify(programme, null, 2)}
      </pre>
      
        <div className="pb-debug-title">Programme Elements (Debug)</div>
        <pre className="pb-debug-content">
          {JSON.stringify(selectedElements, null, 2)}
        </pre>
      </div>
      */}
      
      {renderFloatingComponent()}
      
      {/* Image Modal */}
      <Modal
        isOpen={imageModal.isOpen}
        onClose={closeImageModal}
        title={imageModal.title}
      >
        {imageModal.image ? (
          <div>
            <img 
              src={imageModal.image} 
              alt="Slide Preview" 
              className="pb-modal-image"
            />
            {(imageModal.slideFile || imageModal.slideNumber) && (
              <div className="pb-modal-slide-info">
                {imageModal.slideFile && (
                  <span className="pb-modal-slide-file">
                    <strong>File:</strong> {imageModal.slideFile}
                  </span>
                )}
                {imageModal.slideNumber && (
                  <span className="pb-modal-slide-number">
                    <strong>Slide:</strong> {imageModal.slideNumber}
                  </span>
                )}
              </div>
            )}
            {imageModal.flags && imageModal.flags.length > 0 && (
              <div className="pb-modal-slide-flags">
                <strong>Flags:</strong>{' '}
                {imageModal.flags.join(', ')}
              </div>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            Image not found for this slide
          </div>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={editModal.show}
        onClose={closeEditModal}
        title={`Edit ${editModal.type === 'module' ? 'Module' : editModal.type === 'part' ? 'Part' : 'Block'}`}
      >
        <form onSubmit={(e) => { e.preventDefault(); handleEditSave(); }} className="pb-edit-form">
          <div className="pb-form-group">
            <label htmlFor="edit-name" className="pb-form-label">Name:</label>
            <input autoComplete="off"
              id="edit-name"
              type="text"
              value={editFormData.name || ''}
              onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
              className="pb-form-input"
              placeholder="Enter name"
              required
            />
          </div>

          {/* Public name: only for components that already have one. */}
          {Boolean((editModal.item?.public_name || '').trim()) && (
            <div className="pb-form-group">
              <label htmlFor="edit-public-name" className="pb-form-label">Public name - to be deleted - use name!</label>
              <input autoComplete="off"
                id="edit-public-name"
                type="text"
                value={editFormData.public_name || ''}
                onChange={(e) => setEditFormData(prev => ({ ...prev, public_name: e.target.value }))}
                className="pb-form-input"
                placeholder="Enter public name"
              />
            </div>
          )}
          
          <ComponentDetailFields
            idPrefix="edit"
            type={editModal.type}
            value={editFormData}
            onChange={setEditFormData}
            courses={courses}
          />
          
          <div className="pb-form-actions">
            <button type="button" onClick={closeEditModal} className="pb-btn pb-btn-cancel">
              Cancel
            </button>
            <button type="submit" className="pb-btn pb-btn-save">
              Save
            </button>
          </div>
        </form>
      </Modal>

      {/* Save Modal */}
      <Modal
        isOpen={saveModal}
        onClose={closeSaveModal}
        title="Save"
        boxClassName="modal-wide"
      >
        <div className="pb-save-modal-layout">
          {/* Left: Overwrite existing */}
          <form
            className="pb-save-form pb-save-section"
            onSubmit={(e) => {
              e.preventDefault();
              openOverwriteConfirm();
            }}
          >
            <div className="pb-save-section-title"><strong>Save (overwrite existing)</strong></div>

            <div className="pb-form-group">
              <label htmlFor="overwrite-programme" className="pb-form-label">Select:</label>
              <select autoComplete="off"
                id="overwrite-programme"
                className="pb-form-input"
                value={overwriteKey}
                onChange={(e) => {
                  const key = e.target.value;
                  setOverwriteKey(key);

                  const selected = resolveOverwriteProgramme(key);
                  const type = selected?.type || '';
                  setOverwriteName(selected?.name || '');
                  setOverwriteType(['block', 'part', 'module', 'template', 'custom'].includes(type) ? type : '');
                  setOverwritePublicName(selected?.public_name || '');
                  setOverwriteMeta(selected ? metaFromProgramme(selected) : emptyProgrammeMeta());
                }}
                onFocus={() => loadProgrammes()}
              >
                <option value="">Select...</option>
                {saveableTypeOrder.map((t) => {
                  if (t === 'block' && !topLevelTypeCompatibility.allowBlock) return null;
                  if (t === 'part' && !topLevelTypeCompatibility.allowPart) return null;
                  if (t === 'module' && !topLevelTypeCompatibility.allowModule) return null;
                  return (
                    <optgroup key={t} label={t.charAt(0).toUpperCase() + t.slice(1)}>
                      {saveableProgrammeGroups[t] && saveableProgrammeGroups[t].length ? (
                        saveableProgrammeGroups[t].map((p) => (
                          <option key={p.id ?? makeProgrammeKey(p.type, p.name)} value={p.id}>
                            {p.name}
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>— none —</option>
                      )}
                    </optgroup>
                  );
                })}
              </select>
            </div>

            <div className="pb-form-group">
              <label htmlFor="overwrite-type" className="pb-form-label">Type:</label>
              <select autoComplete="off"
                id="overwrite-type"
                value={overwriteType}
                onChange={(e) => setOverwriteType(e.target.value)}
                className="pb-form-input"
                disabled={!overwriteKey || !['block', 'part', 'module', 'template', 'custom'].includes(resolveOverwriteProgramme(overwriteKey)?.type)}
              >
                <option value="" disabled>Select type...</option>
                <option
                  value="block"
                  disabled={!topLevelTypeCompatibility.allowBlock || resolveOverwriteProgramme(overwriteKey)?.type !== 'block'}
                >
                  Block
                </option>
                <option
                  value="part"
                  disabled={!topLevelTypeCompatibility.allowPart || resolveOverwriteProgramme(overwriteKey)?.type !== 'part'}
                >
                  Part
                </option>
                <option
                  value="module"
                  disabled={!topLevelTypeCompatibility.allowModule || resolveOverwriteProgramme(overwriteKey)?.type !== 'module'}
                >
                  Module
                </option>
                <option value="template">Template</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            <div className="pb-form-group">
              <label htmlFor="overwrite-name" className="pb-form-label">Name:</label>
              <input autoComplete="off"
                id="overwrite-name"
                type="text"
                value={overwriteName}
                onChange={(e) => setOverwriteName(e.target.value)}
                className="pb-form-input"
                placeholder="Name"
              />
            </div>

            {/* Public name: only if the programme being overwritten already has one. */}
            {Boolean(((programmes ?? []).find((p) => p && String(p.id) === String(overwriteKey))?.public_name || '').trim()) && (
              <div className="pb-form-group">
                <label htmlFor="overwrite-public-name" className="pb-form-label">Public name - to be deleted - use name!</label>
                <input autoComplete="off"
                  id="overwrite-public-name"
                  type="text"
                  value={overwritePublicName}
                  onChange={(e) => setOverwritePublicName(e.target.value)}
                  className="pb-form-input"
                  placeholder="Public name (optional)"
                />
              </div>
            )}

            <ProgrammeMetaFields
              idPrefix="overwrite"
              content={programme}
              type={overwriteType}
              meta={overwriteMeta}
              onChange={setOverwriteMeta}
            />
            <ComponentDetailFields
              idPrefix="overwrite"
              type={overwriteType}
              value={overwriteComponentDetails}
              onChange={setOverwriteComponentDetails}
              courses={courses}
            />

            <div className="pb-form-actions pb-save-section-actions">
              <button
                type="submit"
                className="pb-btn pb-btn-save"
                disabled={!overwriteKey || !overwriteName?.trim() || savingProgramme || overwriteConfirmLoading}
              >
                {overwriteConfirmLoading ? 'Checking...' : 'Save'}
              </button>
            </div>
          </form>

          {/* Right: Save new */}
          <form
            className="pb-save-form pb-save-section pb-save-section-right"
            onSubmit={(e) => {
              e.preventDefault();
              handleSaveSubmit();
            }}
          >
            <div className="pb-save-section-title"><strong>Save new</strong></div>

            <div className="pb-form-group">
              <label htmlFor="save-type" className="pb-form-label">Type:</label>
              <select autoComplete="off"
                id="save-type"
                value={saveFormData.type}
                onChange={(e) => setSaveFormData(prev => ({ ...prev, type: e.target.value }))}
                className="pb-form-input"
                required
              >
                <option value="" disabled>Select type...</option>
                <option value="block" disabled={!topLevelTypeCompatibility.allowBlock}>Block</option>
                <option value="part" disabled={!topLevelTypeCompatibility.allowPart}>Part</option>
                <option value="module" disabled={!topLevelTypeCompatibility.allowModule}>Module</option>
                <option value="template">Template</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            <div className="pb-form-group">
              <label htmlFor="save-name" className="pb-form-label">Name:</label>
              <input autoComplete="off"
                id="save-name"
                type="text"
                value={saveFormData.name}
                onChange={(e) => setSaveFormData(prev => ({ ...prev, name: e.target.value }))}
                className="pb-form-input"
                placeholder="Name"
                required
              />
            </div>

            {/* Public name: only if the programme being edited already has one. */}
            {saveNewShowsPublicName && (
              <div className="pb-form-group">
                <label htmlFor="save-public-name" className="pb-form-label">Public name - to be deleted - use name!</label>
                <input autoComplete="off"
                  id="save-public-name"
                  type="text"
                  value={saveFormData.public_name}
                  onChange={(e) => setSaveFormData(prev => ({ ...prev, public_name: e.target.value }))}
                  className="pb-form-input"
                  placeholder="Public name (optional)"
                />
              </div>
            )}

            <ProgrammeMetaFields
              idPrefix="save"
              content={programme}
              type={saveFormData.type}
              meta={saveFormMeta}
              onChange={setSaveFormMeta}
            />
            <ComponentDetailFields
              idPrefix="save"
              type={saveFormData.type}
              value={saveComponentDetails}
              onChange={setSaveComponentDetails}
              courses={courses}
            />

            <div className="pb-form-actions pb-save-section-actions">
              <button
                type="submit"
                className="pb-btn pb-btn-save"
                disabled={savingProgramme || !saveFormData.name?.trim() || !saveFormData.type}
              >
                Save
              </button>
            </div>
          </form>
        </div>

        <div className="pb-form-actions pb-save-modal-footer">
          <button type="button" onClick={closeSaveModal} className="pb-btn pb-btn-cancel">
            Cancel
          </button>
        </div>
      </Modal>

      {/* Confirm Overwrite Modal */}
      <Modal
        isOpen={overwriteConfirmModalOpen}
        onClose={() => setOverwriteConfirmModalOpen(false)}
        title="Confirm Overwrite"
      >
        <div className="form-section">
          {overwriteConfirmCounts && (
            <>
              <div>
                <strong>Currently saved programme:</strong>{' '}
                {overwriteConfirmCounts.existing.components} component{overwriteConfirmCounts.existing.components === 1 ? '' : 's'},{' '}
                {overwriteConfirmCounts.existing.slides} slide{overwriteConfirmCounts.existing.slides === 1 ? '' : 's'}
              </div>
              <div style={{ marginTop: 8 }}>
                <strong>New programme:</strong>{' '}
                {overwriteConfirmCounts.new.components} component{overwriteConfirmCounts.new.components === 1 ? '' : 's'},{' '}
                {overwriteConfirmCounts.new.slides} slide{overwriteConfirmCounts.new.slides === 1 ? '' : 's'}
              </div>
              <div style={{ marginTop: 12 }}>
                Are you sure you want to overwrite this programme?
              </div>
            </>
          )}
          <div className="button-group" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="pb-btn pb-btn-save"
              disabled={savingProgramme}
              onClick={async () => {
                setOverwriteConfirmModalOpen(false);
                await handleOverwriteSubmit();
              }}
            >
              {savingProgramme ? 'Saving...' : 'Confirm'}
            </button>
            <button
              type="button"
              disabled={savingProgramme}
              onClick={() => setOverwriteConfirmModalOpen(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete/Archive Programme Confirm Modal (module/part/block only) */}
      <Modal
        isOpen={deleteProgrammeModalOpen}
        onClose={() => setDeleteProgrammeModalOpen(false)}
        title={deleteProgrammeAction === 'delete' ? 'Confirm delete' : 'Confirm archive'}
      >
        <div className="form-section">
          {deleteProgrammeLoading ? (
            <div>Loading...</div>
          ) : (
            <>
              <div>
                {deleteProgrammeAction === 'delete'
                  ? `Are you sure you want to permanently delete "${actionProgramme?.name}"? This action cannot be undone.`
                  : `Are you sure you want to archive "${actionProgramme?.name}"?`
                }
              </div>
              {(() => {
                if (!Array.isArray(deleteProgrammeUses) || deleteProgrammeUses.length === 0) return null;
                const currentType = (actionProgramme?.type || '').toString();
                const usesList = currentType
                  ? deleteProgrammeUses.filter((u) => ((u?.type || 'archived').toString() !== currentType))
                  : deleteProgrammeUses;

                if (usesList.length === 0) return null;
                const typeOrder = ['custom', 'template', 'module', 'part', 'block', 'archived'];
                const typeLabel = { custom: 'custom programmes', template: 'template programmes', module: 'modules', part: 'parts', block: 'blocks', archived: 'archived' };
                const groups = {};
                for (const u of usesList) {
                  const t = u.type || 'archived';
                  if (!groups[t]) groups[t] = [];
                  groups[t].push(u.name);
                }
                return (
                  <div style={{ marginTop: 8 }}>
                    <div>This component is in use in:</div>
                    {typeOrder.filter(t => groups[t]?.length > 0).map(t => (
                      <div key={t} style={{ marginTop: 6 }}>
                        <strong>{groups[t].length} {typeLabel[t]}</strong>
                        <ul style={{ margin: '2px 0 0 0', paddingLeft: 18 }}>
                          {groups[t].map((name, i) => <li key={i}>{name}</li>)}
                        </ul>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </>
          )}
          <div className="button-group" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="delete-btn"
              disabled={deleteProgrammeLoading}
              onClick={async () => {
                setDeleteProgrammeModalOpen(false);
                await executeProgrammeDeleteAction(actionProgramme);
              }}
            >
              {deleteProgrammeAction === 'delete' ? 'Delete' : 'Archive'}
            </button>
            <button
              type="button"
              disabled={deleteProgrammeLoading}
              onClick={() => setDeleteProgrammeModalOpen(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit Programme Modal */}
      <Modal
        isOpen={editProgrammeModalOpen}
        onClose={closeEditProgrammeModal}
        title="Edit Programme"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleConfirmEditProgramme();
          }}
          className="pb-edit-form"
        >
          <div className="pb-form-group">
            <label htmlFor="pb-edit-programme-type" className="pb-form-label">Type:</label>
            <select autoComplete="off"
              id="pb-edit-programme-type"
              value={editProgrammeType}
              onChange={(e) => setEditProgrammeType(e.target.value)}
              className="pb-form-input"
              disabled={editProgrammeSaving || !editProgrammeTypeEditable}
            >
              <option value="block" disabled={editProgrammeType !== 'block'}>Block</option>
              <option value="part" disabled={editProgrammeType !== 'part'}>Part</option>
              <option value="module" disabled={editProgrammeType !== 'module'}>Module</option>
              <option value="template">Template</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          <div className="pb-form-group">
            <label htmlFor="pb-edit-programme-name" className="pb-form-label">Name:</label>
            <input autoComplete="off"
              id="pb-edit-programme-name"
              type="text"
              value={editProgrammeName}
              onChange={(e) => setEditProgrammeName(e.target.value)}
              className="pb-form-input"
              placeholder="Programme name"
              required
              disabled={editProgrammeSaving}
            />
          </div>

          {/* Public name: only for programmes that already have one. */}
          {Boolean((actionProgramme?.public_name || '').trim()) && (
            <div className="pb-form-group">
              <label htmlFor="pb-edit-programme-public-name" className="pb-form-label">Public name - to be deleted - use name!</label>
              <input autoComplete="off"
                id="pb-edit-programme-public-name"
                type="text"
                value={editProgrammePublicName}
                onChange={(e) => setEditProgrammePublicName(e.target.value)}
                className="pb-form-input"
                placeholder="Public name (optional)"
                disabled={editProgrammeSaving}
              />
            </div>
          )}

          <ProgrammeMetaFields
            idPrefix="pb-edit-programme"
            content={editProgrammeContent}
            type={editProgrammeType}
            meta={editProgrammeMeta}
            onChange={setEditProgrammeMeta}
            disabled={editProgrammeSaving}
          />
          {['block', 'module'].includes(editProgrammeType) && editProgrammeComponentDetails === null ? (
            <p className="pb-form-hint">Loading {editProgrammeType} details…</p>
          ) : (
            <ComponentDetailFields
              idPrefix="pb-edit-programme"
              type={editProgrammeType}
              value={editProgrammeComponentDetails}
              onChange={setEditProgrammeComponentDetails}
              courses={courses}
              disabled={editProgrammeSaving}
            />
          )}

          {editProgrammeError && (
            <div className="pb-error-text" role="alert">
              {editProgrammeError}
            </div>
          )}

          <div className="pb-form-actions">
            <button
              type="button"
              onClick={closeEditProgrammeModal}
              className="pb-btn pb-btn-cancel"
              disabled={editProgrammeSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="pb-btn pb-btn-save"
              disabled={editProgrammeSaving}
            >
              Confirm
            </button>
          </div>
        </form>
      </Modal>

      {/* Export Slideshow Modal */}
      <Modal
        isOpen={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        title="Export Slideshow"
      >
        <form
          onSubmit={(e) => { e.preventDefault(); handleExportSubmit(); }}
          className="pb-edit-form"
        >
          <p className="pb-form-hint">Information to use on cover slides</p>
          <div className="pb-form-group">
            <label htmlFor="export-cd-title" className="pb-form-label">Title:</label>
            <input autoComplete="off"
              id="export-cd-title"
              type="text"
              value={exportCourseDetails.title}
              onChange={(e) => setExportCourseDetails((p) => ({ ...p, title: e.target.value }))}
              className="pb-form-input"
              placeholder="Course Title"
              disabled={exportSubmitting}
            />
          </div>
          <div className="pb-form-group" style={{ display: 'flex', gap: '0.75rem' }}>
            <div style={{ flex: 1 }}>
              <label htmlFor="export-cd-month" className="pb-form-label">Month:</label>
              <input autoComplete="off"
                id="export-cd-month"
                type="text"
                value={exportCourseDetails.month}
                onChange={(e) => setExportCourseDetails((p) => ({ ...p, month: e.target.value }))}
                className="pb-form-input"
                placeholder="Month"
                disabled={exportSubmitting}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label htmlFor="export-cd-year" className="pb-form-label">Year:</label>
              <input autoComplete="off"
                id="export-cd-year"
                type="text"
                value={exportCourseDetails.year}
                onChange={(e) => setExportCourseDetails((p) => ({ ...p, year: e.target.value }))}
                className="pb-form-input"
                placeholder="Year"
                disabled={exportSubmitting}
              />
            </div>
          </div>
          <div className="pb-form-group">
            <label htmlFor="export-cd-tutor" className="pb-form-label">Tutor:</label>
            <input autoComplete="off"
              id="export-cd-tutor"
              type="text"
              value={exportCourseDetails.tutor}
              onChange={(e) => setExportCourseDetails((p) => ({ ...p, tutor: e.target.value }))}
              className="pb-form-input"
              placeholder="Tutor's Name"
              disabled={exportSubmitting}
            />
          </div>
          {exportModulesLoading && <p className="pb-form-hint">Loading modules…</p>}
          {exportModules.length > 1 && (
            <div className="pb-form-group pb-export-modules">
              <div className="pb-export-modules-header">
                <span className="pb-form-label">Modules:</span>
                {/* "Select all" only: shown while any module is unticked. */}
                {exportModules.some((m) => !m.selected) && (
                  <button
                    type="button"
                    className="pb-export-modules-toggle"
                    onClick={() => setExportModules((prev) => prev.map((m) => ({ ...m, selected: true })))}
                    disabled={exportSubmitting}
                  >
                    Select all
                  </button>
                )}
              </div>
              <div className="pb-export-modules-list">
                {exportModules.map((m) => (
                  <label key={m.index} className="pb-export-option">
                    <Checkbox
                      checked={m.selected}
                      onChange={() => setExportModules((prev) => prev.map((x) => (
                        x.index === m.index ? { ...x, selected: !x.selected } : x
                      )))}
                      disabled={exportSubmitting}
                    />
                    <span className="pb-export-option-name">{m.index + 1}. {m.name}</span>
                    <span className="pb-export-option-count">
                      {m.slideCount} slide{m.slideCount === 1 ? '' : 's'}
                    </span>
                  </label>
                ))}
              </div>
              {exportHasLooseSlides && (
                <p className="pb-form-hint pb-export-modules-hint">
                  Slides outside the modules go with the module after them (slides at the end go with the last module).
                </p>
              )}
              <label className="pb-export-option pb-export-separate">
                <Checkbox
                  checked={exportSeparateFiles}
                  onChange={(e) => setExportSeparateFiles(e.target.checked)}
                  disabled={exportSubmitting}
                />
                <span>Export as separate files</span>
              </label>
              {exportNoModulesSelected && <p className="pb-form-hint pb-form-hint--warning">Select at least one module.</p>}
            </div>
          )}
          <div className="pb-form-group pb-export-names">
            <span className="pb-form-label">{exportSeparate ? 'File names:' : 'File name:'}</span>
            {!exportSeparate ? (
              <div className="pb-export-name-row">
                <input
                  autoComplete="off"
                  type="text"
                  className="pb-form-input pb-export-name-account"
                  aria-label="Account"
                  placeholder="Account"
                  value={exportFileNames.account}
                  onChange={(e) => setExportFileNames((p) => ({ ...p, account: e.target.value }))}
                  disabled={exportSubmitting}
                />
                <span className="pb-export-name-text">-</span>
                <input
                  autoComplete="off"
                  type="text"
                  className="pb-form-input pb-export-name-main"
                  aria-label="Programme name"
                  placeholder={exportFileNames.account ? 'Programme name' : 'Programme name (or your name_date-time)'}
                  value={exportFileNames.programme}
                  onChange={(e) => setExportFileNames((p) => ({ ...p, programme: e.target.value }))}
                  disabled={exportSubmitting}
                />
                <input
                  autoComplete="off"
                  type="text"
                  inputMode="numeric"
                  className="pb-form-input pb-export-name-year"
                  aria-label="Year"
                  placeholder="Year"
                  value={exportFileNames.year}
                  onChange={(e) => setExportFileNames((p) => ({ ...p, year: e.target.value }))}
                  disabled={exportSubmitting}
                />
                <span className="pb-export-name-text">.pptx</span>
              </div>
            ) : (
              <>
                <div className="pb-export-name-row">
                  <input
                    autoComplete="off"
                    type="text"
                    className="pb-form-input pb-export-name-main"
                    aria-label="Account"
                    placeholder="Account"
                    value={exportFileNames.account}
                    onChange={(e) => setExportFileNames((p) => ({ ...p, account: e.target.value }))}
                    disabled={exportSubmitting}
                  />
                  <input
                    autoComplete="off"
                    type="text"
                    inputMode="numeric"
                    className="pb-form-input pb-export-name-year"
                    aria-label="Year"
                    placeholder="Year"
                    value={exportFileNames.year}
                    onChange={(e) => setExportFileNames((p) => ({ ...p, year: e.target.value }))}
                    disabled={exportSubmitting}
                  />
                </div>
                <div className="pb-export-names-list">
                  {exportModules.filter((m) => m.selected).map((m) => (
                    <div key={m.index} className="pb-export-name-row">
                      <span className="pb-export-name-text">
                        {exportFileNames.account.trim() || (!exportFileNames.programme.trim() ? 'your name_date-time' : '')} Module {m.index + 1} -
                      </span>
                      <input
                        autoComplete="off"
                        type="text"
                        className="pb-form-input pb-export-name-main"
                        aria-label={`Module ${m.index + 1} name`}
                        placeholder={m.name}
                        value={m.fileName}
                        onChange={(e) => setExportModules((prev) => prev.map((x) => (
                          x.index === m.index ? { ...x, fileName: e.target.value } : x
                        )))}
                        disabled={exportSubmitting}
                      />
                      <span className="pb-export-name-text">
                        {exportFileNames.year.trim() || new Date().getFullYear()}.pptx
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="pb-form-actions">
            <button
              type="button"
              onClick={() => setExportModalOpen(false)}
              className="pb-btn pb-btn-cancel"
              disabled={exportSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="pb-btn pb-btn-save"
              disabled={exportSubmitting || exportModulesLoading || exportNoModulesSelected}
            >
              {exportSubmitting ? 'Exporting…' : 'Export'}
            </button>
          </div>
        </form>
      </Modal>
      </div>
    </div>
  );
}
